import React, { useEffect, useMemo, useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import adminApi from '../services/adminApi';
import IntegrationMerchants from './IntegrationMerchants';
import AdminLocationDebug from './AdminLocationDebug';
import AdminLeadsBilling from './AdminLeadsBilling';
import {
  GST_STATES,
  formatStateLabel,
  resolveOrgStateCode,
  stateCodeFromGstin,
} from '../utils/gstStates';
import '../styles/pages/Admin.css';

function toDateInputValue(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
}

function formatDateOnly(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
}

function formatBillKind(value) {
  const kind = String(value || '').trim().toLowerCase();
  if (kind === 'cycle') return 'Cycle';
  if (kind === 'prorata') return 'Prorata';
  if (!kind) return '-';
  return kind.replace(/_/g, ' ');
}

function formatDuration(ms) {
  if (ms === null || ms === undefined) return '-';
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return '-';
  const totalSec = Math.round(n / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const totalMin = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (totalMin < 60) return sec ? `${totalMin}m ${sec}s` : `${totalMin}m`;
  const hr = Math.floor(totalMin / 60);
  const min = totalMin % 60;
  return min ? `${hr}h ${min}m` : `${hr}h`;
}

function normalizeStatus(value) {
  return String(value || '').trim().toLowerCase();
}

function formatPeriodRange(start, end) {
  if (!start && !end) return '-';
  if (start && end) return `${formatDateOnly(start)} → ${formatDateOnly(end)}`;
  return formatDateOnly(start || end);
}

/** Calendar-ish countdown: "1y 2m 3d", "Due today", or "Overdue by 5d". */
function formatDueIn(value, now = new Date()) {
  if (!value) return null;
  const target = new Date(value);
  if (Number.isNaN(target.getTime())) return null;

  const start = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  const end = new Date(Date.UTC(target.getFullYear(), target.getMonth(), target.getDate()));
  const dayMs = 24 * 60 * 60 * 1000;
  const diffDays = Math.round((end - start) / dayMs);

  if (diffDays === 0) return 'Due today';
  if (diffDays < 0) {
    const overdue = Math.abs(diffDays);
    if (overdue < 30) return `Overdue by ${overdue}d`;
    const months = Math.floor(overdue / 30);
    const days = overdue % 30;
    return days ? `Overdue by ${months}m ${days}d` : `Overdue by ${months}m`;
  }

  let years = end.getUTCFullYear() - start.getUTCFullYear();
  let months = end.getUTCMonth() - start.getUTCMonth();
  let days = end.getUTCDate() - start.getUTCDate();
  if (days < 0) {
    months -= 1;
    const prevMonth = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 0));
    days += prevMonth.getUTCDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  const parts = [];
  if (years > 0) parts.push(`${years}y`);
  if (months > 0) parts.push(`${months}m`);
  if (days > 0 || parts.length === 0) parts.push(`${days}d`);
  return `Due in ${parts.join(' ')}`;
}

function getUpcomingPayment(company) {
  const total = Number(company?.upcomingPaymentAmount || company?.openDuesAmount || 0);
  const base = Number(company?.upcomingPaymentBase || 0);
  const gst = Number(company?.upcomingPaymentGst || 0);
  const dueAt = company?.upcomingPaymentDueAt || company?.periodEnd || company?.trialEndsAt || null;
  const hasAmount = total > 0;
  return {
    amount: hasAmount ? total : null,
    base: hasAmount ? base : null,
    gst: hasAmount ? gst : null,
    dueAt,
    dueLabel: formatDueIn(dueAt)
  };
}

function formatAiAction(action) {
  const key = String(action || '').toUpperCase();
  const labels = {
    RECEIPT_OCR: 'Receipt OCR',
    STATEMENT_PARSE: 'Statement parse',
    EXPENSE_MATCH: 'Expense match',
    DESCRIPTION_GEN: 'Description gen',
    ONBOARDING_OCR: 'Onboarding OCR',
    PAYBILL_OCR: 'Pay bill OCR',
    WHATSAPP_CHAT: 'WhatsApp chat',
    LEADS_SEARCH_PARSE: 'Leads search parse',
    LEADS_ENRICH: 'Leads enrich',
    LEADS_SELLER_PROFILE: 'Leads seller profile',
    LEADS_OUTREACH: 'Leads outreach',
    LEADS_FOLLOWUP: 'Leads follow-up',
    LEADS_REPLY_CLASSIFY: 'Leads reply classify',
    LEADS_WA_TEMPLATES: 'Leads WA templates',
    LEADS_SALES_AGENT: 'Leads sales agent',
    LEADS_TEMPLATE_VARS: 'Leads template vars',
    LEADS_TAKEOVER_SCORE: 'Leads takeover score',
    LEADS_CARD_OCR: 'Leads card OCR',
    LEADS_REGENERATE_SEARCHES: 'Leads regenerate searches',
    LEADS_SERPER: 'Leads Serper',
    NOTES_STT: 'Notes STT',
    NOTES_SUMMARY: 'Notes summary',
    OTHER: 'Other'
  };
  return labels[key] || key.replace(/_/g, ' ').toLowerCase();
}

const OPEN_BILL_STATUSES = new Set(['due', 'failed', 'retrying']);

const Admin = () => {
  const [error, setError] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [adminToken, setAdminToken] = useState(() => localStorage.getItem('adminToken') || null);
  const [adminProfile, setAdminProfile] = useState(() => {
    try {
      const raw = localStorage.getItem('adminProfile');
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  });

  const [tab, setTab] = useState('stats');

  const [dateRange, setDateRange] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 6);
    return { startDate: toDateInputValue(start), endDate: toDateInputValue(end) };
  });

  const [includeTripTotals, setIncludeTripTotals] = useState(true);

  const [overview, setOverview] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);

  const [expandedCompanyId, setExpandedCompanyId] = useState(null);
  const [employeesByCompanyId, setEmployeesByCompanyId] = useState({});
  const [employeesLoadingByCompanyId, setEmployeesLoadingByCompanyId] = useState({});
  const [supervisorsByCompanyId, setSupervisorsByCompanyId] = useState({});
  const [expandedPanel, setExpandedPanel] = useState('employees');

  const [billingCompanies, setBillingCompanies] = useState([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingSearch, setBillingSearch] = useState('');
  const [billingPlanStatus, setBillingPlanStatus] = useState('all');
  const [billingPaymentStatus, setBillingPaymentStatus] = useState('all');
  const [expandedBillingCompanyId, setExpandedBillingCompanyId] = useState(null);
  const [billingDetailsByCompanyId, setBillingDetailsByCompanyId] = useState({});
  const [billingDetailsLoadingByCompanyId, setBillingDetailsLoadingByCompanyId] = useState({});
  const [trialDraftByCompanyId, setTrialDraftByCompanyId] = useState({});
  const [trialSavingByCompanyId, setTrialSavingByCompanyId] = useState({});
  const [trialEditorCompanyId, setTrialEditorCompanyId] = useState(null);
  const [offlineDocsByCompanyId, setOfflineDocsByCompanyId] = useState({});
  const [offlineDocFormByCompanyId, setOfflineDocFormByCompanyId] = useState({});
  const [offlineDocBusyByCompanyId, setOfflineDocBusyByCompanyId] = useState({});
  const [billingModeSavingByCompanyId, setBillingModeSavingByCompanyId] = useState({});

  const [usageAccounts, setUsageAccounts] = useState([]);
  const [usageRates, setUsageRates] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageSearch, setUsageSearch] = useState('');
  const [usageEditsByCompanyId, setUsageEditsByCompanyId] = useState({});
  const [usageSavingByCompanyId, setUsageSavingByCompanyId] = useState({});

  const [aiExpenses, setAiExpenses] = useState(null);
  const [aiExpensesLoading, setAiExpensesLoading] = useState(false);
  const [aiExpensesSearch, setAiExpensesSearch] = useState('');
  const [aiExpensesRecentOpen, setAiExpensesRecentOpen] = useState(false);
  const [aiExpenseFxRate, setAiExpenseFxRate] = useState(84);
  const [aiExpensePgPercent, setAiExpensePgPercent] = useState(2);
  const [platformConfig, setPlatformConfig] = useState({
    orgName: '',
    orgEmail: '',
    orgPhone: '',
    orgWebsite: '',
    orgGstin: '',
    orgStateCode: '',
    orgAddress: '',
    orgLogoUrl: '',
    defaultReceiptPageRate: '',
    defaultStatementPageRate: '',
    defaultReceiptPagesFree: '',
    defaultStatementPagesFree: '',
    defaultLeadsFree: '',
    defaultNoteMinutesFree: '',
    defaultLeadRate: '',
    defaultNoteMinuteRate: '',
    referralLeadsFree: '',
    referralNoteMinutesFree: '',
    gstRate: '0.18'
  });
  const [platformConfigLoading, setPlatformConfigLoading] = useState(false);
  const [platformConfigSaving, setPlatformConfigSaving] = useState(false);
  const [isPlatformConfigEditing, setIsPlatformConfigEditing] = useState(false);
  const [isPlatformConfigExpanded, setIsPlatformConfigExpanded] = useState(false);

  const [demoRequests, setDemoRequests] = useState([]);
  const [demoRequestsLoading, setDemoRequestsLoading] = useState(false);
  const [demoRequestGeneratingId, setDemoRequestGeneratingId] = useState(null);

  const isAuthorized = Boolean(adminToken);

  const fmt = useMemo(() => {
    const n = (v) => (Number.isFinite(Number(v)) ? Number(v).toLocaleString() : '0');
    const km = (v) => (Number.isFinite(Number(v)) ? Number(v).toFixed(1) : '0.0');
    const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 0 })}`;
    return { n, km, money };
  }, []);
  const queuesUrl = useMemo(() => {
    const baseUrl = adminApi?.defaults?.baseURL || window.location.origin;
    try {
      return new URL('/admin/queues', baseUrl).toString();
    } catch (_) {
      return '/admin/queues';
    }
  }, []);

  const logout = () => {
    try {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminProfile');
    } catch (_) {}
    setAdminToken(null);
    setAdminProfile(null);
    setOverview(null);
    setCompanies([]);
    setBillingCompanies([]);
    setExpandedCompanyId(null);
    setEmployeesByCompanyId({});
    setEmployeesLoadingByCompanyId({});
    setSupervisorsByCompanyId({});
    setExpandedBillingCompanyId(null);
    setBillingDetailsByCompanyId({});
    setBillingDetailsLoadingByCompanyId({});
    setTrialDraftByCompanyId({});
    setTrialSavingByCompanyId({});
    setTrialEditorCompanyId(null);
    setUsageAccounts([]);
    setUsageRates(null);
    setUsageEditsByCompanyId({});
    setUsageSavingByCompanyId({});
    setAiExpenses(null);
    setAiExpensesSearch('');
    setAiExpensesRecentOpen(false);
    setPlatformConfig({
      orgName: '',
      orgEmail: '',
      orgPhone: '',
      orgWebsite: '',
      orgGstin: '',
      orgStateCode: '',
      orgAddress: '',
      orgLogoUrl: '',
      defaultReceiptPageRate: '',
      defaultStatementPageRate: '',
      defaultReceiptPagesFree: '',
      defaultStatementPagesFree: '',
      defaultLeadsFree: '',
      defaultNoteMinutesFree: '',
      defaultLeadRate: '',
      defaultNoteMinuteRate: '',
      referralLeadsFree: '',
      referralNoteMinutesFree: '',
      gstRate: '0.18'
    });
    setPlatformConfigLoading(false);
    setPlatformConfigSaving(false);
    setIsPlatformConfigEditing(false);
    setDemoRequests([]);
  };

  const loadStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewResp, companiesResp] = await Promise.all([
        adminApi.get('/api/admin/stats/overview', { params: { ...dateRange } }),
        adminApi.get('/api/admin/companies', { params: { ...dateRange, includeTripTotals } })
      ]);
      setOverview(overviewResp.data);
      setCompanies(companiesResp.data?.companies || []);
    } catch (e) {
      console.error('Failed loading admin stats:', e);
      const msg = e?.response?.data?.message || e?.message || 'Failed to load admin stats';
      setError(msg);
      if (e?.response?.status === 401 || e?.response?.status === 403) logout();
    } finally {
      setLoading(false);
    }
  };

  const loadBilling = async () => {
    setBillingLoading(true);
    setError(null);
    try {
      const resp = await adminApi.fetchBillingCompanies();
      const companies = resp?.companies || [];
      setBillingCompanies(companies);
      setBillingDetailsByCompanyId({});
      setExpandedBillingCompanyId(null);
      setTrialEditorCompanyId(null);
      setTrialDraftByCompanyId(
        companies.reduce((acc, company) => {
          acc[company.companyId] = company.trialEndsAt
            ? toDateInputValue(new Date(company.trialEndsAt))
            : '';
          return acc;
        }, {})
      );
    } catch (e) {
      console.error('Failed loading admin billing:', e);
      const msg = e?.response?.data?.message || e?.message || 'Failed to load admin billing';
      setError(msg);
      if (e?.response?.status === 401 || e?.response?.status === 403) logout();
    } finally {
      setBillingLoading(false);
    }
  };

  const loadUsage = async () => {
    setUsageLoading(true);
    setError(null);
    try {
      const resp = await adminApi.fetchUsageAccounts();
      const companies = resp?.companies || [];
      setUsageAccounts(companies);
      setUsageRates(resp?.rates || null);
      setUsageEditsByCompanyId(
        companies.reduce((acc, company) => {
          acc[company.companyId] = {
            receiptPagesFree: String(Number(company.receiptPagesFree || 0)),
            statementPagesFree: String(Number(company.statementPagesFree || 0)),
            receiptPageRate: company.receiptPageRate === null || company.receiptPageRate === undefined ? '' : String(company.receiptPageRate),
            statementPageRate: company.statementPageRate === null || company.statementPageRate === undefined ? '' : String(company.statementPageRate)
          };
          return acc;
        }, {})
      );
    } catch (e) {
      console.error('Failed loading admin usage:', e);
      const msg = e?.response?.data?.message || e?.message || 'Failed to load usage accounts';
      setError(msg);
      if (e?.response?.status === 401 || e?.response?.status === 403) logout();
    } finally {
      setUsageLoading(false);
    }
  };

  const loadAiExpenses = async () => {
    setAiExpensesLoading(true);
    setError(null);
    try {
      const resp = await adminApi.fetchAiExpenses({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
      setAiExpenses(resp || null);
      const fx = Number(resp?.usdInrRate);
      if (Number.isFinite(fx) && fx > 0) setAiExpenseFxRate(fx);
    } catch (e) {
      console.error('Failed loading AI expenses:', e);
      const msg = e?.response?.data?.message || e?.message || 'Failed to load AI expenses';
      setError(msg);
      if (e?.response?.status === 401 || e?.response?.status === 403) logout();
    } finally {
      setAiExpensesLoading(false);
    }
  };

  const loadPlatformConfig = async () => {
    setPlatformConfigLoading(true);
    setError(null);
    try {
      const resp = await adminApi.fetchPlatformConfig();
      const cfg = resp?.config || {};
      const form = {
        orgName: String(cfg.orgName || ''),
        orgEmail: String(cfg.orgEmail || ''),
        orgPhone: String(cfg.orgPhone || ''),
        orgWebsite: String(cfg.orgWebsite || ''),
        orgGstin: String(cfg.orgGstin || ''),
        orgStateCode: resolveOrgStateCode(cfg.orgGstin, cfg.orgStateCode),
        orgAddress: String(cfg.orgAddress || ''),
        orgLogoUrl: String(cfg.orgLogoUrl || ''),
        defaultReceiptPageRate: cfg.defaultReceiptPageRate === null || cfg.defaultReceiptPageRate === undefined ? '' : String(cfg.defaultReceiptPageRate),
        defaultStatementPageRate: cfg.defaultStatementPageRate === null || cfg.defaultStatementPageRate === undefined ? '' : String(cfg.defaultStatementPageRate),
        defaultReceiptPagesFree: cfg.defaultReceiptPagesFree === null || cfg.defaultReceiptPagesFree === undefined ? '' : String(cfg.defaultReceiptPagesFree),
        defaultStatementPagesFree: cfg.defaultStatementPagesFree === null || cfg.defaultStatementPagesFree === undefined ? '' : String(cfg.defaultStatementPagesFree),
        defaultLeadsFree: cfg.defaultLeadsFree === null || cfg.defaultLeadsFree === undefined ? '' : String(cfg.defaultLeadsFree),
        defaultNoteMinutesFree: cfg.defaultNoteMinutesFree === null || cfg.defaultNoteMinutesFree === undefined ? '' : String(cfg.defaultNoteMinutesFree),
        defaultLeadRate: cfg.defaultLeadRate === null || cfg.defaultLeadRate === undefined ? '' : String(cfg.defaultLeadRate),
        defaultNoteMinuteRate: cfg.defaultNoteMinuteRate === null || cfg.defaultNoteMinuteRate === undefined ? '' : String(cfg.defaultNoteMinuteRate),
        referralLeadsFree: cfg.referralLeadsFree === null || cfg.referralLeadsFree === undefined ? '' : String(cfg.referralLeadsFree),
        referralNoteMinutesFree: cfg.referralNoteMinutesFree === null || cfg.referralNoteMinutesFree === undefined ? '' : String(cfg.referralNoteMinutesFree),
        gstRate: String(cfg.gstRate ?? '')
      };
      setPlatformConfig(form);
      setUsageRates((prev) => ({
        ...(prev || {}),
        gstRate: Number(cfg.gstRate || 0),
        defaultReceiptPageRate: Number(cfg.defaultReceiptPageRate ?? 5),
        defaultStatementPageRate: Number(cfg.defaultStatementPageRate ?? 8),
        defaultReceiptPagesFree: Number(cfg.defaultReceiptPagesFree ?? 5),
        defaultStatementPagesFree: Number(cfg.defaultStatementPagesFree ?? 2)
      }));
    } catch (e) {
      console.error('Failed loading platform config:', e);
      const msg = e?.response?.data?.message || e?.message || 'Failed to load platform settings';
      setError(msg);
      if (e?.response?.status === 401 || e?.response?.status === 403) logout();
    } finally {
      setPlatformConfigLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthorized) return;
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized]);

  useEffect(() => {
    if (!isAuthorized) return;
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.startDate, dateRange.endDate, includeTripTotals]);

  useEffect(() => {
    if (!isAuthorized || tab !== 'billing') return;
    loadBilling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized, tab]);

  useEffect(() => {
    if (!isAuthorized || tab !== 'usage') return;
    loadUsage();
    loadPlatformConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized, tab]);

  useEffect(() => {
    if (!isAuthorized || tab !== 'expenses') return;
    loadAiExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized, tab, dateRange.startDate, dateRange.endDate]);

  const loadDemoRequests = async () => {
    setDemoRequestsLoading(true);
    setError(null);
    try {
      const resp = await adminApi.get('/api/admin/demo-requests');
      setDemoRequests(resp.data?.requests || []);
    } catch (e) {
      console.error('Failed loading demo requests:', e);
      const msg = e?.response?.data?.message || e?.message || 'Failed to load demo requests';
      setError(msg);
      if (e?.response?.status === 401 || e?.response?.status === 403) logout();
    } finally {
      setDemoRequestsLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthorized || tab !== 'demoRequests') return;
    loadDemoRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized, tab]);

  const handleGenerateInvite = async (id) => {
    setDemoRequestGeneratingId(id);
    setError(null);
    try {
      await adminApi.post(`/api/admin/demo-requests/${id}/generate-invite`);
      await loadDemoRequests();
    } catch (e) {
      console.error('Failed to generate invite:', e);
      const msg = e?.response?.data?.message || e?.message || 'Failed to generate invite';
      setError(msg);
    } finally {
      setDemoRequestGeneratingId(null);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsAuthLoading(true);
    setError(null);
    try {
      const resp = await adminApi.post('/api/admin/auth/google', {
        idToken: credentialResponse.credential
      });
      const token = resp.data?.token;
      const profile = resp.data?.superadmin || null;
      if (!token) throw new Error('Missing token from server');

      localStorage.setItem('adminToken', token);
      localStorage.setItem('adminProfile', JSON.stringify(profile));
      setAdminToken(token);
      setAdminProfile(profile);
      setTab('stats');
    } catch (e) {
      console.error('Admin login failed:', e);
      const msg = e?.response?.data?.message || e?.message || 'Admin login failed';
      setError(msg);
    } finally {
      setIsAuthLoading(false);
    }
  };

  const handleGoogleError = () => setError('Google authentication failed');

  const openCompany = async (companyId, panel = 'employees') => {
    if (expandedCompanyId === companyId && expandedPanel === panel) {
      setExpandedCompanyId(null);
      return;
    }
    setExpandedCompanyId(companyId);
    setExpandedPanel(panel);

    if (employeesByCompanyId[companyId] && supervisorsByCompanyId[companyId]) return;
    setEmployeesLoadingByCompanyId((prev) => ({ ...prev, [companyId]: true }));
    try {
      const resp = await adminApi.get(`/api/admin/companies/${companyId}/employees`, {
        params: { ...dateRange }
      });
      setEmployeesByCompanyId((prev) => ({ ...prev, [companyId]: resp.data?.employees || [] }));
      setSupervisorsByCompanyId((prev) => ({ ...prev, [companyId]: resp.data?.supervisors || [] }));
    } catch (e) {
      console.error('Failed loading company employees:', e);
      const msg = e?.response?.data?.message || e?.message || 'Failed to load company employees';
      setError(msg);
    } finally {
      setEmployeesLoadingByCompanyId((prev) => ({ ...prev, [companyId]: false }));
    }
  };

  const emptyOfflineDocForm = () => ({
    editingId: null,
    kind: 'invoice',
    documentNumber: '',
    description: '',
    amount: '',
    dueOn: '',
    paidOn: '',
    s3Key: '',
    contentType: '',
    ocrSnapshot: null,
    hasExistingFile: false
  });

  const openBillingCompany = async (companyId) => {
    if (expandedBillingCompanyId === companyId) {
      setExpandedBillingCompanyId(null);
      return;
    }
    setExpandedBillingCompanyId(companyId);
    setOfflineDocFormByCompanyId((prev) => ({
      ...prev,
      [companyId]: prev[companyId] || emptyOfflineDocForm()
    }));
    if (Object.prototype.hasOwnProperty.call(billingDetailsByCompanyId, companyId)
      && Array.isArray(billingDetailsByCompanyId[companyId])) {
      return;
    }

    setBillingDetailsLoadingByCompanyId((prev) => ({ ...prev, [companyId]: true }));
    try {
      const resp = await adminApi.fetchCompanyBillingPayments(companyId);
      const bills = Array.isArray(resp?.bills) ? resp.bills : [];
      const docs = Array.isArray(resp?.offlineDocuments) ? resp.offlineDocuments : [];
      setBillingDetailsByCompanyId((prev) => ({ ...prev, [companyId]: bills }));
      setOfflineDocsByCompanyId((prev) => ({ ...prev, [companyId]: docs }));
    } catch (e) {
      console.error('Failed loading company billing bills:', e);
      const msg = e?.response?.data?.message || e?.message || 'Failed to load company billing bills';
      setError(msg);
    } finally {
      setBillingDetailsLoadingByCompanyId((prev) => ({ ...prev, [companyId]: false }));
    }
  };

  const handleToggleBillingMode = async (companyId, nextMode) => {
    setBillingModeSavingByCompanyId((prev) => ({ ...prev, [companyId]: true }));
    setError(null);
    try {
      await adminApi.updateCompanyBillingMode(companyId, nextMode);
      await loadBilling();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to update billing mode';
      setError(msg);
    } finally {
      setBillingModeSavingByCompanyId((prev) => ({ ...prev, [companyId]: false }));
    }
  };

  const handleOfflineDocField = (companyId, field, value) => {
    setOfflineDocFormByCompanyId((prev) => ({
      ...prev,
      [companyId]: {
        ...(prev[companyId] || emptyOfflineDocForm()),
        [field]: value
      }
    }));
  };

  const handleOfflineDocOcr = async (companyId, file) => {
    if (!file) return;
    setOfflineDocBusyByCompanyId((prev) => ({ ...prev, [companyId]: true }));
    setError(null);
    try {
      const resp = await adminApi.ocrAttendanceBillingDocument(companyId, file);
      const prefill = resp?.prefill || {};
      setOfflineDocFormByCompanyId((prev) => {
        const current = prev[companyId] || emptyOfflineDocForm();
        return {
          ...prev,
          [companyId]: {
            ...current,
            documentNumber: prefill.documentNumber || current.documentNumber || '',
            description: prefill.description || current.description || '',
            amount: prefill.amount != null ? String(prefill.amount) : (current.amount || ''),
            // Prefill dates only when empty so cleared fields stay empty
            dueOn: current.dueOn || prefill.dueOn || '',
            paidOn: current.paidOn || '',
            s3Key: resp?.s3Key || current.s3Key || '',
            contentType: resp?.contentType || file.type || current.contentType || 'application/pdf',
            ocrSnapshot: prefill.ocrSnapshot || null,
            hasExistingFile: Boolean(resp?.s3Key || current.hasExistingFile)
          }
        };
      });
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'OCR failed';
      setError(msg);
    } finally {
      setOfflineDocBusyByCompanyId((prev) => ({ ...prev, [companyId]: false }));
    }
  };

  const handleEditOfflineDoc = (companyId, doc) => {
    setOfflineDocFormByCompanyId((prev) => ({
      ...prev,
      [companyId]: {
        editingId: doc.id,
        kind: doc.kind === 'proforma' ? 'proforma' : 'invoice',
        documentNumber: doc.documentNumber || '',
        description: doc.description || '',
        amount: doc.amount != null ? String(doc.amount) : '',
        dueOn: doc.dueOn ? String(doc.dueOn).slice(0, 10) : '',
        paidOn: doc.paidOn ? String(doc.paidOn).slice(0, 10) : '',
        s3Key: '',
        contentType: doc.contentType || 'application/pdf',
        ocrSnapshot: null,
        hasExistingFile: Boolean(doc.openUrl)
      }
    }));
  };

  const handleCancelOfflineDocEdit = (companyId) => {
    setOfflineDocFormByCompanyId((prev) => ({ ...prev, [companyId]: emptyOfflineDocForm() }));
  };

  const handleSaveOfflineDoc = async (companyId) => {
    const form = offlineDocFormByCompanyId[companyId] || emptyOfflineDocForm();
    const isEdit = Boolean(form.editingId);
    if (!isEdit && !form.s3Key) {
      setError('Run OCR / upload a PDF first so the file is stored');
      return;
    }
    if (!String(form.documentNumber || '').trim()) {
      setError('Document number is required');
      return;
    }
    setOfflineDocBusyByCompanyId((prev) => ({ ...prev, [companyId]: true }));
    setError(null);
    try {
      const payload = {
        kind: form.kind,
        documentNumber: form.documentNumber,
        description: form.description,
        amount: form.amount,
        // Explicit null clears dates (empty string allowed)
        dueOn: form.dueOn ? form.dueOn : null,
        paidOn: form.paidOn ? form.paidOn : null,
        contentType: form.contentType || 'application/pdf',
        ocrSnapshot: form.ocrSnapshot || null
      };
      if (form.s3Key) payload.s3Key = form.s3Key;

      if (isEdit) {
        await adminApi.updateAttendanceBillingDocument(companyId, form.editingId, payload);
      } else {
        await adminApi.createAttendanceBillingDocument(companyId, payload);
      }
      const docsResp = await adminApi.fetchAttendanceBillingDocuments(companyId);
      setOfflineDocsByCompanyId((prev) => ({
        ...prev,
        [companyId]: Array.isArray(docsResp?.documents) ? docsResp.documents : []
      }));
      setOfflineDocFormByCompanyId((prev) => ({ ...prev, [companyId]: emptyOfflineDocForm() }));
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to save document';
      setError(msg);
    } finally {
      setOfflineDocBusyByCompanyId((prev) => ({ ...prev, [companyId]: false }));
    }
  };

  const handleDeleteOfflineDoc = async (companyId, documentId) => {
    setOfflineDocBusyByCompanyId((prev) => ({ ...prev, [companyId]: true }));
    setError(null);
    try {
      await adminApi.deleteAttendanceBillingDocument(companyId, documentId);
      setOfflineDocsByCompanyId((prev) => ({
        ...prev,
        [companyId]: (prev[companyId] || []).filter((d) => d.id !== documentId)
      }));
      const form = offlineDocFormByCompanyId[companyId];
      if (form?.editingId === documentId) {
        setOfflineDocFormByCompanyId((prev) => ({ ...prev, [companyId]: emptyOfflineDocForm() }));
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to delete document';
      setError(msg);
    } finally {
      setOfflineDocBusyByCompanyId((prev) => ({ ...prev, [companyId]: false }));
    }
  };

  const handleSaveTrialDate = async (companyId) => {
    const trialEndsAt = String(trialDraftByCompanyId[companyId] || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(trialEndsAt)) {
      setError('Pick a valid trial end date (YYYY-MM-DD)');
      return;
    }
    setTrialSavingByCompanyId((prev) => ({ ...prev, [companyId]: true }));
    setError(null);
    try {
      await adminApi.updateCompanyTrial(companyId, { trialEndsAt });
      setTrialEditorCompanyId(null);
      await loadBilling();
    } catch (e) {
      console.error('Failed updating trial date:', e);
      const msg = e?.response?.data?.message || e?.message || 'Failed to update trial date';
      setError(msg);
    } finally {
      setTrialSavingByCompanyId((prev) => ({ ...prev, [companyId]: false }));
    }
  };

  const handleUsageEdit = (companyId, field, value) => {
    let nextValue = String(value || '');
    if (field === 'receiptPageRate' || field === 'statementPageRate') {
      nextValue = nextValue.replace(/[^0-9.]/g, '');
      const firstDot = nextValue.indexOf('.');
      if (firstDot !== -1) {
        nextValue = `${nextValue.slice(0, firstDot + 1)}${nextValue.slice(firstDot + 1).replace(/\./g, '')}`;
      }
    } else {
      nextValue = nextValue.replace(/\D/g, '');
    }
    setUsageEditsByCompanyId((prev) => ({
      ...prev,
      [companyId]: {
        receiptPagesFree: prev[companyId]?.receiptPagesFree || '0',
        statementPagesFree: prev[companyId]?.statementPagesFree || '0',
        receiptPageRate: prev[companyId]?.receiptPageRate || '',
        statementPageRate: prev[companyId]?.statementPageRate || '',
        [field]: nextValue
      }
    }));
  };

  const handleUsageSave = async (companyId) => {
    const edit = usageEditsByCompanyId[companyId] || {};
    const receiptPagesFree = Number(edit.receiptPagesFree || 0);
    const statementPagesFree = Number(edit.statementPagesFree || 0);
    const receiptPageRate = String(edit.receiptPageRate || '').trim() === '' ? null : Number(edit.receiptPageRate);
    const statementPageRate = String(edit.statementPageRate || '').trim() === '' ? null : Number(edit.statementPageRate);
    if ((receiptPageRate !== null && (!Number.isFinite(receiptPageRate) || receiptPageRate < 0))
      || (statementPageRate !== null && (!Number.isFinite(statementPageRate) || statementPageRate < 0))) {
      setError('Rate fields must be non-negative numbers (or blank for defaults).');
      return;
    }
    setUsageSavingByCompanyId((prev) => ({ ...prev, [companyId]: true }));
    setError(null);
    try {
      const resp = await adminApi.updateUsageAccount(companyId, {
        receiptPagesFree,
        statementPagesFree,
        receiptPageRate,
        statementPageRate
      });
      const updated = resp?.account || null;
      setUsageRates(resp?.rates || usageRates);
      if (updated) {
        setUsageAccounts((prev) => prev.map((row) => (
          Number(row.companyId) === Number(companyId)
            ? {
              ...row,
              receiptPagesFree: Number(updated.receiptPagesFree || 0),
              statementPagesFree: Number(updated.statementPagesFree || 0),
              receiptPagesUsed: Number(updated.receiptPagesUsed || 0),
              statementPagesUsed: Number(updated.statementPagesUsed || 0),
              receiptPageRate: updated.receiptPageRate === null || updated.receiptPageRate === undefined ? null : Number(updated.receiptPageRate),
              statementPageRate: updated.statementPageRate === null || updated.statementPageRate === undefined ? null : Number(updated.statementPageRate),
              effectiveReceiptPageRate: Number(updated.effectiveReceiptPageRate || 0),
              effectiveStatementPageRate: Number(updated.effectiveStatementPageRate || 0),
              receiptPagesFreeRemaining: Number(updated.receiptPagesFree || 0),
              statementPagesFreeRemaining: Number(updated.statementPagesFree || 0)
            }
            : row
        )));
        setUsageEditsByCompanyId((prev) => ({
          ...prev,
          [companyId]: {
            receiptPagesFree: String(Number(updated.receiptPagesFree || 0)),
            statementPagesFree: String(Number(updated.statementPagesFree || 0)),
            receiptPageRate: updated.receiptPageRate === null || updated.receiptPageRate === undefined ? '' : String(updated.receiptPageRate),
            statementPageRate: updated.statementPageRate === null || updated.statementPageRate === undefined ? '' : String(updated.statementPageRate)
          }
        }));
      }
    } catch (e) {
      console.error('Failed updating usage account:', e);
      const msg = e?.response?.data?.message || e?.message || 'Failed to update usage account';
      setError(msg);
    } finally {
      setUsageSavingByCompanyId((prev) => ({ ...prev, [companyId]: false }));
    }
  };

  const handlePlatformConfigChange = (field, value) => {
    setPlatformConfig((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'orgGstin') {
        const code = stateCodeFromGstin(value);
        if (code) next.orgStateCode = code;
      }
      return next;
    });
  };

  const handleCancelPlatformConfigEdit = () => {
    setIsPlatformConfigEditing(false);
    loadPlatformConfig();
  };

  const handleStartPlatformConfigEdit = () => {
    setIsPlatformConfigExpanded(true);
    setIsPlatformConfigEditing(true);
  };

  const formatPlatformTextValue = (value, fallback = '-') => {
    if (value === null || value === undefined || value === '') return fallback;
    return String(value);
  };

  const renderPlatformField = ({
    label,
    field,
    type = 'text',
    placeholder,
    step,
    min,
    transform,
    fallback = '-',
    fullWidth = false
  }) => (
    <div className={`admin-usage-platform-field${fullWidth ? ' is-full' : ''}`}>
      <span className="admin-usage-platform-field-label">{label}</span>
      {isPlatformConfigEditing ? (
        <input
          type={type}
          value={platformConfig[field] ?? ''}
          placeholder={placeholder}
          step={step}
          min={min}
          onChange={(e) => handlePlatformConfigChange(field, transform ? transform(e.target.value) : e.target.value)}
        />
      ) : (
        <span className="admin-usage-platform-field-value">
          {formatPlatformTextValue(platformConfig[field], fallback)}
        </span>
      )}
    </div>
  );

  const handleSavePlatformConfig = async () => {
    setPlatformConfigSaving(true);
    setError(null);
    try {
      const payload = {
        orgName: platformConfig.orgName,
        orgEmail: platformConfig.orgEmail,
        orgPhone: platformConfig.orgPhone,
        orgWebsite: platformConfig.orgWebsite,
        orgGstin: platformConfig.orgGstin,
        orgStateCode: platformConfig.orgStateCode,
        orgAddress: platformConfig.orgAddress,
        orgLogoUrl: platformConfig.orgLogoUrl,
        defaultReceiptPageRate: platformConfig.defaultReceiptPageRate === '' ? null : Number(platformConfig.defaultReceiptPageRate),
        defaultStatementPageRate: platformConfig.defaultStatementPageRate === '' ? null : Number(platformConfig.defaultStatementPageRate),
        defaultReceiptPagesFree: platformConfig.defaultReceiptPagesFree === '' ? null : Number(platformConfig.defaultReceiptPagesFree),
        defaultStatementPagesFree: platformConfig.defaultStatementPagesFree === '' ? null : Number(platformConfig.defaultStatementPagesFree),
        defaultLeadsFree: platformConfig.defaultLeadsFree === '' ? null : Number(platformConfig.defaultLeadsFree),
        defaultNoteMinutesFree: platformConfig.defaultNoteMinutesFree === '' ? null : Number(platformConfig.defaultNoteMinutesFree),
        defaultLeadRate: platformConfig.defaultLeadRate === '' ? null : Number(platformConfig.defaultLeadRate),
        defaultNoteMinuteRate: platformConfig.defaultNoteMinuteRate === '' ? null : Number(platformConfig.defaultNoteMinuteRate),
        referralLeadsFree: platformConfig.referralLeadsFree === '' ? null : Number(platformConfig.referralLeadsFree),
        referralNoteMinutesFree:
          platformConfig.referralNoteMinutesFree === ''
            ? null
            : Number(platformConfig.referralNoteMinutesFree),
        gstRate: platformConfig.gstRate === '' || platformConfig.gstRate == null
          ? null
          : Number(platformConfig.gstRate)
      };
      const resp = await adminApi.updatePlatformConfig(payload);
      const cfg = resp?.config || {};
      setPlatformConfig({
        orgName: String(cfg.orgName || ''),
        orgEmail: String(cfg.orgEmail || ''),
        orgPhone: String(cfg.orgPhone || ''),
        orgWebsite: String(cfg.orgWebsite || ''),
        orgGstin: String(cfg.orgGstin || ''),
        orgStateCode: resolveOrgStateCode(cfg.orgGstin, cfg.orgStateCode),
        orgAddress: String(cfg.orgAddress || ''),
        orgLogoUrl: String(cfg.orgLogoUrl || ''),
        defaultReceiptPageRate: cfg.defaultReceiptPageRate === null || cfg.defaultReceiptPageRate === undefined ? '' : String(cfg.defaultReceiptPageRate),
        defaultStatementPageRate: cfg.defaultStatementPageRate === null || cfg.defaultStatementPageRate === undefined ? '' : String(cfg.defaultStatementPageRate),
        defaultReceiptPagesFree: cfg.defaultReceiptPagesFree === null || cfg.defaultReceiptPagesFree === undefined ? '' : String(cfg.defaultReceiptPagesFree),
        defaultStatementPagesFree: cfg.defaultStatementPagesFree === null || cfg.defaultStatementPagesFree === undefined ? '' : String(cfg.defaultStatementPagesFree),
        defaultLeadsFree: cfg.defaultLeadsFree === null || cfg.defaultLeadsFree === undefined ? '' : String(cfg.defaultLeadsFree),
        defaultNoteMinutesFree: cfg.defaultNoteMinutesFree === null || cfg.defaultNoteMinutesFree === undefined ? '' : String(cfg.defaultNoteMinutesFree),
        defaultLeadRate: cfg.defaultLeadRate === null || cfg.defaultLeadRate === undefined ? '' : String(cfg.defaultLeadRate),
        defaultNoteMinuteRate: cfg.defaultNoteMinuteRate === null || cfg.defaultNoteMinuteRate === undefined ? '' : String(cfg.defaultNoteMinuteRate),
        referralLeadsFree: cfg.referralLeadsFree === null || cfg.referralLeadsFree === undefined ? '' : String(cfg.referralLeadsFree),
        referralNoteMinutesFree: cfg.referralNoteMinutesFree === null || cfg.referralNoteMinutesFree === undefined ? '' : String(cfg.referralNoteMinutesFree),
        gstRate: String(cfg.gstRate ?? '')
      });
      setUsageRates((prev) => ({
        ...(prev || {}),
        gstRate: Number(cfg.gstRate || 0),
        defaultReceiptPageRate: Number(cfg.defaultReceiptPageRate ?? 5),
        defaultStatementPageRate: Number(cfg.defaultStatementPageRate ?? 8),
        defaultReceiptPagesFree: Number(cfg.defaultReceiptPagesFree ?? 5),
        defaultStatementPagesFree: Number(cfg.defaultStatementPagesFree ?? 2)
      }));
      setIsPlatformConfigEditing(false);
      await loadUsage();
    } catch (e) {
      console.error('Failed updating platform config:', e);
      const msg = e?.response?.data?.message || e?.message || 'Failed to update platform settings';
      setError(msg);
    } finally {
      setPlatformConfigSaving(false);
    }
  };

  const filteredBillingCompanies = useMemo(() => {
    return billingCompanies.filter((company) => {
      const matchesSearch = !billingSearch.trim() || `${company.companyName || ''} ${company.email || ''}`.toLowerCase().includes(billingSearch.trim().toLowerCase());
      const matchesPlanStatus = billingPlanStatus === 'all' || String(company.planStatus || '') === billingPlanStatus;
      const latestStatus = normalizeStatus(company.latestBillStatus);
      const matchesPaymentStatus = billingPaymentStatus === 'all'
        || latestStatus === billingPaymentStatus
        || (billingPaymentStatus === 'open' && OPEN_BILL_STATUSES.has(latestStatus));
      return matchesSearch && matchesPlanStatus && matchesPaymentStatus;
    });
  }, [billingCompanies, billingPlanStatus, billingPaymentStatus, billingSearch]);

  const billingSummary = useMemo(() => {
    return filteredBillingCompanies.reduce(
      (acc, company) => {
        const planStatus = normalizeStatus(company.planStatus);
        const latestStatus = normalizeStatus(company.latestBillStatus);
        const totalPaidAmount = Number(company.totalPaidAmount || 0);
        const openDuesAmount = Number(company.openDuesAmount || 0);
        const openBillCount = Number(company.openBillCount || 0);
        const hasPaid = totalPaidAmount > 0 || latestStatus === 'paid';

        acc.companyCount += 1;
        acc.activePlans += planStatus === 'active' ? 1 : 0;
        acc.trialPlans += planStatus === 'trial' ? 1 : 0;
        acc.paidCompanies += hasPaid ? 1 : 0;
        acc.totalCollected += totalPaidAmount;
        acc.openDues += openDuesAmount;
        acc.openBillCount += openBillCount;
        return acc;
      },
      {
        companyCount: 0,
        activePlans: 0,
        trialPlans: 0,
        paidCompanies: 0,
        totalCollected: 0,
        openDues: 0,
        openBillCount: 0
      }
    );
  }, [filteredBillingCompanies]);

  const filteredUsageAccounts = useMemo(() => {
    const needle = usageSearch.trim().toLowerCase();
    if (!needle) return usageAccounts;
    return usageAccounts.filter((row) => `${row.companyName || ''} ${row.email || ''}`.toLowerCase().includes(needle));
  }, [usageAccounts, usageSearch]);

  const aiExpenseFx = useMemo(() => {
    const n = Number(aiExpenseFxRate);
    return Number.isFinite(n) && n > 0 ? n : 84;
  }, [aiExpenseFxRate]);

  const aiExpensePgRate = useMemo(() => {
    const n = Number(aiExpensePgPercent);
    return Number.isFinite(n) && n >= 0 ? n / 100 : 0.02;
  }, [aiExpensePgPercent]);

  const usdToInrWithFx = (usd, fx = aiExpenseFx) =>
    Math.round((Number(usd || 0) * fx + Number.EPSILON) * 10000) / 10000;

  const pgChargeFromInclGst = (inclGst, rate = aiExpensePgRate) =>
    Math.round((Number(inclGst || 0) * rate + Number.EPSILON) * 100) / 100;

  const filteredAiExpenseCompanies = useMemo(() => {
    const rows = Array.isArray(aiExpenses?.companies) ? aiExpenses.companies : [];
    const needle = aiExpensesSearch.trim().toLowerCase();
    const filtered = !needle
      ? rows
      : rows.filter((row) => `${row.companyName || ''} ${row.email || ''}`.toLowerCase().includes(needle));
    return filtered.map((row) => {
      const costUsd = Number(row.costUsd || 0);
      return {
        ...row,
        costInr: usdToInrWithFx(costUsd),
        byAction: (row.byAction || []).map((actionRow) => ({
          ...actionRow,
          costInr: usdToInrWithFx(actionRow.costUsd),
          byModel: (actionRow.byModel || []).map((m) => ({
            ...m,
            costInr: usdToInrWithFx(m.costUsd)
          }))
        }))
      };
    });
  }, [aiExpenses, aiExpensesSearch, aiExpenseFx]);

  const aiExpenseSummary = useMemo(() => {
    const base = aiExpenses?.summary || {
      totalCalls: 0,
      totalTokens: 0,
      totalPromptTokens: 0,
      totalCompletionTokens: 0,
      totalCostUsd: 0,
      totalCostInr: 0,
      totalPaymentsReceivedExGst: 0,
      totalPaymentsReceivedInclGst: 0,
      totalPaymentCount: 0,
      totalCreditsLeftValue: 0,
      totalCreditsLeftPaid: 0,
      totalCreditsLeftFreeValue: 0,
      byAction: []
    };
    const totalCostUsd = Number(base.totalCostUsd || 0);
    const totalPaymentsInclGst = Number(base.totalPaymentsReceivedInclGst || 0);
    return {
      ...base,
      totalCostInr: usdToInrWithFx(totalCostUsd),
      totalPaymentsReceivedInclGst: totalPaymentsInclGst,
      totalPgCharge: pgChargeFromInclGst(totalPaymentsInclGst),
      byAction: (base.byAction || []).map((row) => ({
        ...row,
        costInr: usdToInrWithFx(row.costUsd),
        byModel: (row.byModel || []).map((m) => ({
          ...m,
          costInr: usdToInrWithFx(m.costUsd)
        }))
      }))
    };
  }, [aiExpenses, aiExpenseFx, aiExpensePgRate]);

  const aiExpenseRecent = useMemo(() => {
    return (aiExpenses?.recent || []).map((row) => ({
      ...row,
      costInr: usdToInrWithFx(row.costUsd)
    }));
  }, [aiExpenses, aiExpenseFx]);

  const usageSummary = useMemo(() => {
    return filteredUsageAccounts.reduce(
      (acc, row) => {
        const s = row.sessionStats || {};
        acc.companyCount += 1;
        acc.receiptPagesUsed += Number(row.receiptPagesUsed || 0);
        acc.statementPagesUsed += Number(row.statementPagesUsed || 0);
        acc.receiptPagesFree += Number(row.receiptPagesFree || 0);
        acc.statementPagesFree += Number(row.statementPagesFree || 0);
        acc.receiptPagesRecharged += Number(row.receiptPagesRecharged || 0);
        acc.statementPagesRecharged += Number(row.statementPagesRecharged || 0);
        acc.amountRecharged += Number(row.amountRecharged || 0);
        acc.rechargeCount += Number(row.rechargeCount || 0);
        acc.sessionCount += Number(s.sessionCount || 0);
        acc.completedSessions += Number(s.completedSessions || 0);
        acc.receiptsCount += Number(s.receiptsCount || 0);
        acc.statementsCount += Number(s.statementsCount || 0);
        acc.totalChargedInr += Number(s.totalChargedInr || 0);
        acc.totalInternalInr += Number(s.totalInternalInr || 0);
        if (Number.isFinite(Number(s.avgDurationMs))) {
          acc.totalDurationMs += Number(s.avgDurationMs) * Number(s.sessionCount || 0);
          acc.durationSampleTotal += Number(s.sessionCount || 0);
        }
        return acc;
      },
      {
        companyCount: 0,
        receiptPagesUsed: 0,
        statementPagesUsed: 0,
        receiptPagesFree: 0,
        statementPagesFree: 0,
        receiptPagesRecharged: 0,
        statementPagesRecharged: 0,
        amountRecharged: 0,
        rechargeCount: 0,
        sessionCount: 0,
        completedSessions: 0,
        receiptsCount: 0,
        statementsCount: 0,
        totalChargedInr: 0,
        totalInternalInr: 0,
        totalDurationMs: 0,
        durationSampleTotal: 0
      }
    );
  }, [filteredUsageAccounts]);

  const usageMarginInr = usageSummary.totalChargedInr - usageSummary.totalInternalInr;
  const usageMarginPercent = usageSummary.totalChargedInr > 0
    ? (usageMarginInr / usageSummary.totalChargedInr) * 100
    : null;
  const avgSessionDurationMs = usageSummary.durationSampleTotal > 0
    ? Math.round(usageSummary.totalDurationMs / usageSummary.durationSampleTotal)
    : null;

  if (!isAuthorized) {
    return (
      <div className="login-container">
        <div className="login-box">
          <h1>Admin</h1>
          <p>Sign in with a superadmin account to view platform stats.</p>

          <div className="login-buttons">
            <div className="google-login-button">
              <GoogleLogin onSuccess={handleGoogleSuccess} onError={handleGoogleError} theme="filled_black" />
            </div>
          </div>

          {isAuthLoading && (
            <div className="modern-loading-state" style={{ minHeight: 120, padding: 16 }}>
              <div className="loading-spinner-container">
                <div className="loading-spinner modern" />
                <div className="loading-text">
                  <p>Signing you in…</p>
                </div>
              </div>
            </div>
          )}

          {error && <div className="admin-error" role="alert">{error}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Admin Dashboard</h1>
        <div className="page-actions">
          <div className="admin-tabs" role="tablist" aria-label="Admin tabs">
            <button type="button" className={`admin-tab-btn ${tab === 'stats' ? 'active' : ''}`} onClick={() => setTab('stats')}>
              Stats
            </button>
            <button type="button" className={`admin-tab-btn ${tab === 'billing' ? 'active' : ''}`} onClick={() => setTab('billing')}>
              Billing
            </button>
            <button type="button" className={`admin-tab-btn ${tab === 'usage' ? 'active' : ''}`} onClick={() => setTab('usage')}>
              AI Usage
            </button>
            <button type="button" className={`admin-tab-btn ${tab === 'leadsBilling' ? 'active' : ''}`} onClick={() => setTab('leadsBilling')}>
              Leads Billing
            </button>
            <button type="button" className={`admin-tab-btn ${tab === 'expenses' ? 'active' : ''}`} onClick={() => setTab('expenses')}>
              Expenses
            </button>
            <button type="button" className={`admin-tab-btn ${tab === 'demoRequests' ? 'active' : ''}`} onClick={() => setTab('demoRequests')}>
              Demo Requests
            </button>
            <button type="button" className={`admin-tab-btn ${tab === 'integrations' ? 'active' : ''}`} onClick={() => setTab('integrations')}>
              Integrations
            </button>
            <button type="button" className={`admin-tab-btn ${tab === 'locationDebug' ? 'active' : ''}`} onClick={() => setTab('locationDebug')}>
              Location Debug
            </button>
            <button type="button" className={`admin-tab-btn ${tab === 'queues' ? 'active' : ''}`} onClick={() => setTab('queues')}>
              Queues
            </button>
          </div>
          <button type="button" className="btn-secondary small" onClick={logout}>
            Sign out{adminProfile?.email ? ` (${adminProfile.email})` : ''}
          </button>
        </div>
      </div>

      <div className="page-content">
        {tab === 'queues' ? (
          <div className="admin-iframe-shell">
            <iframe src={queuesUrl} title="Bull Board" />
          </div>
        ) : tab === 'integrations' ? (
          <IntegrationMerchants />
        ) : tab === 'locationDebug' ? (
          <AdminLocationDebug />
        ) : tab === 'demoRequests' ? (
          <>
            <div className="admin-inline-controls admin-usage-controls">
              <button type="button" className="btn-secondary small" onClick={loadDemoRequests} disabled={demoRequestsLoading}>
                Refresh
              </button>
            </div>
            {demoRequestsLoading ? (
              <div className="modern-loading-state">
                <div className="loading-spinner-container">
                  <div className="loading-spinner modern" />
                  <div className="loading-text">
                    <h3>Loading demo requests</h3>
                    <p>Fetching demo access requests.</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Company</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Emps</th>
                      <th>State</th>
                      <th>How heard</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {demoRequests.map((req) => (
                      <tr key={req.id}>
                        <td style={{ fontWeight: 700 }}>{req.name || '-'}</td>
                        <td>{req.companyName || '-'}</td>
                        <td className="muted">{req.email || '-'}</td>
                        <td className="muted">{req.phone || '-'}</td>
                        <td>{req.numberOfEmployees || '-'}</td>
                        <td>{req.state || '-'}</td>
                        <td>{req.howYouHeardAboutUs || '-'}</td>
                        <td>
                          <span className={`admin-pill ${req.status === 'APPROVED' ? '' : ''}`}>{req.status || '-'}</span>
                        </td>
                        <td className="muted">{formatDateTime(req.createdAt)}</td>
                        <td>
                          {req.status === 'PENDING' ? (
                            <button
                              type="button"
                              className="admin-link-btn"
                              onClick={() => handleGenerateInvite(req.id)}
                              disabled={demoRequestGeneratingId === req.id}
                            >
                              {demoRequestGeneratingId === req.id ? 'Generating...' : 'Generate Invite'}
                            </button>
                          ) : (
                            <span className="muted">Invite sent</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {demoRequests.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="muted">No demo requests found.</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            )}
            {error && <div className="admin-error" role="alert">{error}</div>}
          </>
        ) : tab === 'leadsBilling' ? (
          <>
            <AdminLeadsBilling onError={setError} />
            {error && <div className="admin-error" role="alert">{error}</div>}
          </>
        ) : tab === 'usage' ? (
          <div className="admin-usage-tab">
            <div className="admin-inline-controls admin-usage-controls">
              <div className="control-group">
                <label>Search company</label>
                <input
                  type="text"
                  value={usageSearch}
                  placeholder="Name or email"
                  onChange={(e) => setUsageSearch(e.target.value)}
                />
              </div>
              <button type="button" className="btn-secondary small" onClick={loadUsage} disabled={usageLoading}>
                Refresh
              </button>
            </div>

            <div className={`dashboard-card admin-usage-platform-card ${isPlatformConfigExpanded ? 'is-expanded' : 'is-collapsed'}`}>
              <button
                type="button"
                className="admin-usage-platform-toggle"
                onClick={() => setIsPlatformConfigExpanded((v) => !v)}
                aria-expanded={isPlatformConfigExpanded}
                aria-controls="admin-usage-platform-body"
              >
                <div className="admin-usage-platform-toggle-text">
                  <h3>Platform Tax / invoice issuer</h3>
                  <div className="admin-usage-platform-summary">
                    <span className="admin-usage-platform-chip">
                      <span className="muted">Org</span>
                      <strong>{formatPlatformTextValue(platformConfig.orgName, 'Not set')}</strong>
                    </span>
                    <span className="admin-usage-platform-chip">
                      <span className="muted">GSTIN</span>
                      <strong>{formatPlatformTextValue(platformConfig.orgGstin, '-')}</strong>
                    </span>
                    <span className="admin-usage-platform-chip">
                      <span className="muted">State</span>
                      <strong>
                        {formatStateLabel(platformConfig.orgStateCode) ||
                          formatPlatformTextValue(platformConfig.orgStateCode, '-')}
                      </strong>
                    </span>
                    <span className="admin-usage-platform-chip">
                      <span className="muted">GST</span>
                      <strong>{((Number(platformConfig.gstRate || 0) * 100).toFixed(2))}%</strong>
                    </span>
                    <span className="admin-usage-platform-chip">
                      <span className="muted">Default rates</span>
                      <strong>
                        ₹{formatPlatformTextValue(platformConfig.defaultReceiptPageRate, '5')}
                        {' / '}
                        ₹{formatPlatformTextValue(platformConfig.defaultStatementPageRate, '8')}
                      </strong>
                    </span>
                    <span className="admin-usage-platform-chip">
                      <span className="muted">Free pages</span>
                      <strong>
                        {formatPlatformTextValue(platformConfig.defaultReceiptPagesFree, '5')}
                        {' / '}
                        {formatPlatformTextValue(platformConfig.defaultStatementPagesFree, '2')}
                      </strong>
                    </span>
                  </div>
                </div>
                <span className={`admin-usage-platform-chevron ${isPlatformConfigExpanded ? 'is-open' : ''}`} aria-hidden>
                  ▾
                </span>
              </button>

              {isPlatformConfigExpanded ? (
                <div id="admin-usage-platform-body" className="admin-usage-platform-body">
                  <div className="admin-usage-platform-body-header">
                    <p className="muted">Organisation, tax &amp; default usage settings used on invoices and per-company billing fallbacks.</p>
                    {isPlatformConfigEditing ? (
                      <div className="admin-usage-platform-body-actions">
                        <button
                          type="button"
                          className="btn-secondary small admin-outline-btn admin-outline-btn-sm"
                          onClick={handleCancelPlatformConfigEdit}
                          disabled={platformConfigSaving}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn-secondary small admin-outline-btn admin-outline-btn-sm admin-outline-btn-primary"
                          onClick={handleSavePlatformConfig}
                          disabled={platformConfigSaving}
                        >
                          {platformConfigSaving ? 'Saving…' : 'Save settings'}
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="btn-secondary small admin-outline-btn admin-outline-btn-sm"
                        onClick={handleStartPlatformConfigEdit}
                        disabled={platformConfigLoading}
                      >
                        Edit
                      </button>
                    )}
                  </div>

                  {platformConfigLoading ? (
                    <div className="muted">Loading platform settings…</div>
                  ) : (
                    <div className="admin-usage-platform-grid">
                      <div className="admin-usage-platform-section">
                        <h4>Organisation</h4>
                        {renderPlatformField({ label: 'Org name', field: 'orgName' })}
                        {renderPlatformField({ label: 'Org email', field: 'orgEmail', type: 'email' })}
                        {renderPlatformField({ label: 'Org phone', field: 'orgPhone' })}
                        {renderPlatformField({ label: 'Org website', field: 'orgWebsite' })}
                        {renderPlatformField({ label: 'Logo URL', field: 'orgLogoUrl' })}
                        {renderPlatformField({ label: 'Org address', field: 'orgAddress', fullWidth: true })}
                      </div>
                      <div className="admin-usage-platform-section">
                        <h4>Tax (invoice issuer)</h4>
                        {renderPlatformField({
                          label: 'GSTIN',
                          field: 'orgGstin',
                          transform: (v) => String(v || '').toUpperCase().slice(0, 15),
                          placeholder: '15-char GSTIN',
                        })}
                        <div className="admin-usage-platform-field">
                          <span className="admin-usage-platform-field-label">State (GST)</span>
                          {isPlatformConfigEditing ? (
                            <select
                              value={platformConfig.orgStateCode || ''}
                              disabled={Boolean(stateCodeFromGstin(platformConfig.orgGstin))}
                              onChange={(e) =>
                                handlePlatformConfigChange('orgStateCode', e.target.value)
                              }
                            >
                              <option value="">Select state</option>
                              {GST_STATES.map((s) => (
                                <option key={s.code} value={s.code}>
                                  {s.name} ({s.code})
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="admin-usage-platform-field-value">
                              {formatStateLabel(platformConfig.orgStateCode) || '-'}
                            </span>
                          )}
                          {stateCodeFromGstin(platformConfig.orgGstin) ? (
                            <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
                              Locked from GSTIN prefix - required for CGST/SGST vs IGST.
                            </p>
                          ) : (
                            <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
                              Use a 2-digit GST state code (not a name). Without this, all invoices use IGST.
                            </p>
                          )}
                        </div>
                        {renderPlatformField({
                          label: 'GST rate (0.18 or 18)',
                          field: 'gstRate',
                          type: 'number',
                          step: '0.0001',
                          min: '0',
                          placeholder: '0.18'
                        })}
                        <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
                          Stored as a fraction. Enter <code>0.18</code> or <code>18</code> for 18%.
                        </p>
                      </div>
                      <div className="admin-usage-platform-section">
                        <h4>Default usage</h4>
                        {renderPlatformField({ label: 'Receipt rate (₹/page)', field: 'defaultReceiptPageRate', type: 'number', step: '0.01', min: '0', placeholder: '5', fallback: 'Auto (5)' })}
                        {renderPlatformField({ label: 'Statement rate (₹/page)', field: 'defaultStatementPageRate', type: 'number', step: '0.01', min: '0', placeholder: '8', fallback: 'Auto (8)' })}
                        {renderPlatformField({ label: 'Receipt pages free', field: 'defaultReceiptPagesFree', type: 'number', step: '1', min: '0', placeholder: '5', fallback: 'Auto (5)' })}
                        {renderPlatformField({ label: 'Statement pages free', field: 'defaultStatementPagesFree', type: 'number', step: '1', min: '0', placeholder: '2', fallback: 'Auto (2)' })}
                      </div>
                      <div className="admin-usage-platform-section">
                        <h4>Leads joining grant</h4>
                        {renderPlatformField({
                          label: 'Free credits (joining)',
                          field: 'defaultLeadsFree',
                          type: 'number',
                          step: '1',
                          min: '0',
                          placeholder: '200',
                          fallback: 'Auto (200)',
                        })}
                        {renderPlatformField({
                          label: 'Free note mins',
                          field: 'defaultNoteMinutesFree',
                          type: 'number',
                          step: '1',
                          min: '0',
                          placeholder: '60',
                          fallback: 'Auto (60)',
                        })}
                        {renderPlatformField({
                          label: 'Lead rate (credits/lead)',
                          field: 'defaultLeadRate',
                          type: 'number',
                          step: '0.01',
                          min: '0',
                          placeholder: '1',
                          fallback: 'Auto (1)',
                        })}
                        {renderPlatformField({
                          label: 'Note min rate (₹)',
                          field: 'defaultNoteMinuteRate',
                          type: 'number',
                          step: '0.01',
                          min: '0',
                          placeholder: '1.5',
                          fallback: 'Auto (1.5)',
                        })}
                        <p className="muted" style={{ margin: '4px 0 0', fontSize: 12, gridColumn: '1 / -1' }}>
                          Applied to new companies on welcome grant. Blank = code default (200 leads / 60 mins).
                        </p>
                      </div>
                      <div className="admin-usage-platform-section">
                        <h4>Leads referral reward</h4>
                        {renderPlatformField({
                          label: 'Referral free credits',
                          field: 'referralLeadsFree',
                          type: 'number',
                          step: '1',
                          min: '0',
                          placeholder: '50',
                          fallback: 'Auto (50)',
                        })}
                        {renderPlatformField({
                          label: 'Referral free note mins',
                          field: 'referralNoteMinutesFree',
                          type: 'number',
                          step: '1',
                          min: '0',
                          placeholder: '30',
                          fallback: 'Auto (30)',
                        })}
                        <p className="muted" style={{ margin: '4px 0 0', fontSize: 12, gridColumn: '1 / -1' }}>
                          Granted to the inviter after the invitee&apos;s first paid top-up. Blank = code default (50 leads / 30 mins).
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            <div className="admin-usage-dashboard">
              <div className="admin-usage-section-header">
                <div>
                  <h3>AI usage &amp; cost</h3>
                  <p className="muted">Charged to customers vs actual internal OCR/LLM cost across reconciliation sessions.</p>
                </div>
              </div>
              <div className="admin-stats-grid admin-billing-stats-grid admin-usage-summary-grid admin-usage-revenue-grid">
                <div className="dashboard-card admin-usage-metric-card">
                  <h3>Charged to customers</h3>
                  <div className="stat-value">{fmt.money(usageSummary.totalChargedInr)}</div>
                  <div className="muted">Pay-as-you-go session charges</div>
                </div>
                <div className="dashboard-card admin-usage-metric-card">
                  <h3>Actual internal cost</h3>
                  <div className="stat-value">{fmt.money(usageSummary.totalInternalInr)}</div>
                  <div className="muted">OCR, LLM &amp; infra cost incurred</div>
                </div>
                <div className={`dashboard-card admin-usage-metric-card ${usageMarginInr >= 0 ? 'admin-usage-metric-positive' : 'admin-usage-metric-negative'}`}>
                  <h3>Net margin</h3>
                  <div className="stat-value">{fmt.money(usageMarginInr)}</div>
                  <div className="muted">
                    {usageMarginPercent === null ? 'No charged revenue yet' : `${usageMarginPercent.toFixed(1)}% of charged`}
                  </div>
                </div>
                <div className="dashboard-card admin-usage-metric-card">
                  <h3>Top-up revenue</h3>
                  <div className="stat-value">{fmt.money(usageSummary.amountRecharged)}</div>
                  <div className="muted">{fmt.n(usageSummary.rechargeCount)} prepaid transactions</div>
                </div>
              </div>

              <div className="admin-usage-section-header">
                <div>
                  <h3>Session activity</h3>
                  <p className="muted">Throughput and processing time across reconciliation sessions.</p>
                </div>
              </div>
              <div className="admin-stats-grid admin-billing-stats-grid admin-usage-summary-grid">
                <div className="dashboard-card">
                  <h3>Sessions</h3>
                  <div className="stat-value">{fmt.n(usageSummary.sessionCount)}</div>
                  <div className="muted">Completed: {fmt.n(usageSummary.completedSessions)}</div>
                </div>
                <div className="dashboard-card">
                  <h3>Avg session time</h3>
                  <div className="stat-value">{formatDuration(avgSessionDurationMs)}</div>
                  <div className="muted">Upload → matching complete</div>
                </div>
                <div className="dashboard-card">
                  <h3>Receipts processed</h3>
                  <div className="stat-value">{fmt.n(usageSummary.receiptsCount)}</div>
                  <div className="muted">Pages used: {fmt.n(usageSummary.receiptPagesUsed)}</div>
                </div>
                <div className="dashboard-card">
                  <h3>Statements processed</h3>
                  <div className="stat-value">{fmt.n(usageSummary.statementsCount)}</div>
                  <div className="muted">Pages used: {fmt.n(usageSummary.statementPagesUsed)}</div>
                </div>
              </div>

              <div className="admin-usage-section-header admin-usage-section-header-compact">
                <div>
                  <h3>Company roster</h3>
                  <p className="muted">
                    {fmt.n(usageSummary.companyCount)} companies · GST {((Number(usageRates?.gstRate || 0) * 100).toFixed(2))}%
                  </p>
                </div>
              </div>
            </div>

            {usageLoading ? (
              <div className="modern-loading-state">
                <div className="loading-spinner-container">
                  <div className="loading-spinner modern" />
                  <div className="loading-text">
                    <h3>Loading usage accounts</h3>
                    <p>Fetching per-company free limits and consumption.</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="admin-table-wrap admin-usage-table-wrap">
                <table className="admin-table admin-usage-table">
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>
                        <div>Free limits</div>
                        <div className="muted admin-usage-header-sub">Receipt / Statement</div>
                      </th>
                      <th>
                        <div>Rates per page</div>
                        <div className="muted admin-usage-header-sub">Receipt / Statement</div>
                      </th>
                      <th>
                        <div>Charged pages / amount</div>
                        <div className="muted admin-usage-header-sub">Billed to customer</div>
                      </th>
                      <th>
                        <div>Actual pages / amount</div>
                        <div className="muted admin-usage-header-sub">Consumption &amp; internal cost</div>
                      </th>
                      <th>
                        <div>Sessions</div>
                        <div className="muted admin-usage-header-sub">Throughput &amp; margin</div>
                      </th>
                      <th className="admin-usage-col-actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsageAccounts.map((row) => {
                      const edit = usageEditsByCompanyId[row.companyId] || {
                        receiptPagesFree: String(Number(row.receiptPagesFree || 0)),
                        statementPagesFree: String(Number(row.statementPagesFree || 0)),
                        receiptPageRate: row.receiptPageRate === null || row.receiptPageRate === undefined ? '' : String(row.receiptPageRate),
                        statementPageRate: row.statementPageRate === null || row.statementPageRate === undefined ? '' : String(row.statementPageRate)
                      };
                      const isSaving = Boolean(usageSavingByCompanyId[row.companyId]);
                      const sStats = row.sessionStats || {
                        sessionCount: 0, completedSessions: 0, receiptsCount: 0, statementsCount: 0,
                        totalChargedInr: 0, totalInternalInr: 0, marginInr: 0, avgDurationMs: null, lastSessionAt: null
                      };
                      const rowMarginInr = Number(sStats.marginInr || 0);
                      const rowMarginPct = Number(sStats.totalChargedInr || 0) > 0
                        ? (rowMarginInr / Number(sStats.totalChargedInr)) * 100
                        : null;
                      const chargedPagesTotal = Number(row.receiptPagesRecharged || 0) + Number(row.statementPagesRecharged || 0);
                      const actualPagesTotal = Number(row.receiptPagesUsed || 0) + Number(row.statementPagesUsed || 0);
                      return (
                        <tr key={row.companyId}>
                          <td className="admin-usage-cell-company">
                            <div className="admin-usage-company-name">{row.companyName || '-'}</div>
                            <div className="muted admin-usage-company-email">{row.email || '-'}</div>
                          </td>
                          <td>
                            <div className="admin-usage-row-inputs">
                              <input
                                type="text"
                                inputMode="numeric"
                                value={edit.receiptPagesFree}
                                onChange={(e) => handleUsageEdit(row.companyId, 'receiptPagesFree', e.target.value)}
                                aria-label={`Receipt free pages ${row.companyName || row.companyId}`}
                              />
                              <input
                                type="text"
                                inputMode="numeric"
                                value={edit.statementPagesFree}
                                onChange={(e) => handleUsageEdit(row.companyId, 'statementPagesFree', e.target.value)}
                                aria-label={`Statement free pages ${row.companyName || row.companyId}`}
                              />
                            </div>
                            <div className="muted admin-usage-subtext">Receipt free / Statement free</div>
                          </td>
                          <td>
                            <div className="admin-usage-row-inputs">
                              <input
                                type="text"
                                inputMode="decimal"
                                value={edit.receiptPageRate}
                                onChange={(e) => handleUsageEdit(row.companyId, 'receiptPageRate', e.target.value)}
                                aria-label={`Receipt rate ${row.companyName || row.companyId}`}
                                placeholder="5"
                              />
                              <input
                                type="text"
                                inputMode="decimal"
                                value={edit.statementPageRate}
                                onChange={(e) => handleUsageEdit(row.companyId, 'statementPageRate', e.target.value)}
                                aria-label={`Statement rate ${row.companyName || row.companyId}`}
                                placeholder="8"
                              />
                            </div>
                            <div className="muted admin-usage-subtext">
                              Effective: ₹{fmt.n(row.effectiveReceiptPageRate || 0)} / ₹{fmt.n(row.effectiveStatementPageRate || 0)} (blank = defaults)
                            </div>
                          </td>
                          <td>
                            <div className="admin-usage-metric-row">
                              <span className="admin-usage-metric-label">Receipt</span>
                              <span className="admin-usage-metric-pages">{fmt.n(row.receiptPagesRecharged || 0)} pg</span>
                            </div>
                            <div className="admin-usage-metric-row">
                              <span className="admin-usage-metric-label">Statement</span>
                              <span className="admin-usage-metric-pages">{fmt.n(row.statementPagesRecharged || 0)} pg</span>
                            </div>
                            <div className="admin-usage-metric-total">
                              <span>{fmt.n(chargedPagesTotal)} pg · <strong>{fmt.money(row.amountRecharged || 0)}</strong></span>
                            </div>
                            <div className="muted admin-usage-subtext">{fmt.n(row.rechargeCount || 0)} top-up txns</div>
                          </td>
                          <td>
                            <div className="admin-usage-metric-row">
                              <span className="admin-usage-metric-label">Receipt</span>
                              <span className="admin-usage-metric-pages">{fmt.n(row.receiptPagesUsed || 0)} pg</span>
                            </div>
                            <div className="admin-usage-metric-row">
                              <span className="admin-usage-metric-label">Statement</span>
                              <span className="admin-usage-metric-pages">{fmt.n(row.statementPagesUsed || 0)} pg</span>
                            </div>
                            <div className="admin-usage-metric-total">
                              <span>{fmt.n(actualPagesTotal)} pg · <strong>{fmt.money(sStats.totalInternalInr || 0)}</strong></span>
                            </div>
                            <div className="muted admin-usage-subtext">Internal processing cost (OCR + LLM)</div>
                          </td>
                          <td>
                            <div className="admin-usage-metric-row">
                              <span className="admin-usage-metric-label">Sessions</span>
                              <span className="admin-usage-metric-pages">{fmt.n(sStats.sessionCount || 0)}</span>
                            </div>
                            <div className="admin-usage-metric-row">
                              <span className="admin-usage-metric-label">Avg time</span>
                              <span className="admin-usage-metric-pages">{formatDuration(sStats.avgDurationMs)}</span>
                            </div>
                            <div className="admin-usage-metric-row">
                              <span className="admin-usage-metric-label">Rcpt / Stmt</span>
                              <span className="admin-usage-metric-pages">{fmt.n(sStats.receiptsCount || 0)} / {fmt.n(sStats.statementsCount || 0)}</span>
                            </div>
                            <div className={`admin-usage-margin-pill ${rowMarginInr >= 0 ? 'is-positive' : 'is-negative'}`}>
                              {fmt.money(rowMarginInr)}{rowMarginPct === null ? '' : ` · ${rowMarginPct.toFixed(0)}%`}
                            </div>
                          </td>
                          <td className="admin-usage-col-actions">
                            <button
                              type="button"
                              className="admin-outline-btn admin-outline-btn-sm"
                              onClick={() => handleUsageSave(row.companyId)}
                              disabled={isSaving}
                            >
                              {isSaving ? 'Saving...' : 'Save'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredUsageAccounts.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="muted">No usage accounts found.</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            )}
            {error && <div className="admin-error" role="alert">{error}</div>}
          </div>
        ) : tab === 'expenses' ? (
          <>
            <div className="admin-inline-controls admin-usage-controls">
              <div className="control-group">
                <label>Start date</label>
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: e.target.value }))}
                />
              </div>
              <div className="control-group">
                <label>End date</label>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value }))}
                />
              </div>
              <div className="control-group">
                <label>FX rate (USD→INR)</label>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  value={aiExpenseFxRate}
                  onChange={(e) => setAiExpenseFxRate(e.target.value)}
                />
              </div>
              <div className="control-group">
                <label>PG charge %</label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={aiExpensePgPercent}
                  onChange={(e) => setAiExpensePgPercent(e.target.value)}
                />
              </div>
              <div className="control-group">
                <label>Search company</label>
                <input
                  type="text"
                  value={aiExpensesSearch}
                  placeholder="Name or email"
                  onChange={(e) => setAiExpensesSearch(e.target.value)}
                />
              </div>
              <button type="button" className="btn-secondary small" onClick={loadAiExpenses} disabled={aiExpensesLoading}>
                Refresh
              </button>
            </div>

            <div className="admin-billing-dashboard">
              <div className="admin-stats-grid admin-billing-stats-grid">
                <div className="dashboard-card">
                  <h3>Total AI cost</h3>
                  <div className="stat-value">{fmt.money(aiExpenseSummary.totalCostInr)}</div>
                  <div className="muted">USD {Number(aiExpenseSummary.totalCostUsd || 0).toFixed(4)} · FX {aiExpenseFx}</div>
                </div>
                <div className="dashboard-card">
                  <h3>Leads payments (ex-GST)</h3>
                  <div className="stat-value">{fmt.money(aiExpenseSummary.totalPaymentsReceivedExGst)}</div>
                  <div className="muted">
                    {fmt.n(aiExpenseSummary.totalPaymentCount)} paid · incl. GST {fmt.money(aiExpenseSummary.totalPaymentsReceivedInclGst || 0)}
                  </div>
                </div>
                <div className="dashboard-card">
                  <h3>PG charge</h3>
                  <div className="stat-value">{fmt.money(aiExpenseSummary.totalPgCharge || 0)}</div>
                  <div className="muted">{Number(aiExpensePgPercent) || 0}% of GST-incl payments</div>
                </div>
                <div className="dashboard-card">
                  <h3>Credits left (free + paid)</h3>
                  <div className="stat-value">{fmt.money(aiExpenseSummary.totalCreditsLeftValue)}</div>
                  <div className="muted">
                    {fmt.money(aiExpenseSummary.totalCreditsLeftPaid)} paid · {fmt.money(aiExpenseSummary.totalCreditsLeftFreeValue)} free (leads + note mins)
                  </div>
                </div>
                <div className="dashboard-card">
                  <h3>Calls</h3>
                  <div className="stat-value">{fmt.n(aiExpenseSummary.totalCalls)}</div>
                  <div className="muted">AI processing log rows in range</div>
                </div>
                <div className="dashboard-card">
                  <h3>Tokens</h3>
                  <div className="stat-value">{fmt.n(aiExpenseSummary.totalTokens)}</div>
                  <div className="muted">
                    {fmt.n(aiExpenseSummary.totalPromptTokens)} in · {fmt.n(aiExpenseSummary.totalCompletionTokens)} out
                  </div>
                </div>
                <div className="dashboard-card">
                  <h3>Companies</h3>
                  <div className="stat-value">{fmt.n(filteredAiExpenseCompanies.length)}</div>
                  <div className="muted">With AI activity in range</div>
                </div>
              </div>

              <div className="admin-stats-grid admin-billing-stats-grid admin-billing-stats-grid-secondary">
                {(aiExpenseSummary.byAction || []).map((row) => {
                  const modelLines = (row.byModel || []).map((m) => {
                    const name = m.rateKey || m.model || 'unknown';
                    const hasRates = m.inputUsdPer1M != null && m.outputUsdPer1M != null;
                    if (!hasRates) return name;
                    const fmtUsd = (v) => {
                      const n = Number(v);
                      if (!Number.isFinite(n)) return '-';
                      return `$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}`;
                    };
                    return `${name} · ${fmtUsd(m.inputUsdPer1M)} / ${fmtUsd(m.outputUsdPer1M)} per 1M`;
                  });
                  return (
                    <div className="dashboard-card" key={row.action}>
                      <h3>{formatAiAction(row.action)}</h3>
                      <div className="stat-value">{fmt.money(row.costInr)}</div>
                      <div className="muted">
                        {fmt.n(row.calls)} calls · {fmt.n(row.promptTokens)} in · {fmt.n(row.completionTokens)} out
                      </div>
                      <div className="muted">
                        {modelLines.length ? modelLines.join(' · ') : '-'}
                      </div>
                    </div>
                  );
                })}
                {(aiExpenseSummary.byAction || []).length === 0 ? (
                  <div className="dashboard-card">
                    <h3>By action</h3>
                    <div className="stat-value">-</div>
                    <div className="muted">No AI spend in this range</div>
                  </div>
                ) : null}
              </div>
            </div>

            {aiExpensesLoading ? (
              <div className="modern-loading-state">
                <div className="loading-spinner-container">
                  <div className="loading-spinner modern" />
                  <div className="loading-text">
                    <h3>Loading AI expenses</h3>
                    <p>Aggregating onboarding OCR, pay-bill OCR, WhatsApp, leads, and recon AI logs.</p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Company</th>
                        <th>Calls</th>
                        <th>Tokens</th>
                        <th>Cost</th>
                        <th>Payments (ex-GST)</th>
                        <th>Credits left</th>
                        <th>By action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAiExpenseCompanies.map((row) => (
                        <tr key={row.companyId}>
                          <td>
                            <div style={{ fontWeight: 700 }}>{row.companyName}</div>
                            <div className="muted">{row.email || '-'}</div>
                          </td>
                          <td>{fmt.n(row.calls)}</td>
                          <td>{fmt.n(row.tokens)}</td>
                          <td style={{ fontWeight: 700 }}>{fmt.money(row.costInr)}</td>
                          <td>
                            <div style={{ fontWeight: 700 }}>{fmt.money(row.paymentsReceivedExGst || 0)}</div>
                            <div className="muted">
                              {fmt.n(row.paymentCount || 0)} paid · incl. GST {fmt.money(row.paymentsReceivedInclGst || 0)}
                            </div>
                          </td>
                          <td>
                            <div style={{ fontWeight: 700 }}>{fmt.money(row.creditsLeftValue || 0)}</div>
                            <div className="muted">
                              {fmt.money(row.creditsLeftPaid || 0)} paid · {fmt.money(row.creditsLeftFreeValue || 0)} free
                            </div>
                            <div className="muted">
                              {fmt.n(row.leadsFreeRemaining || 0)} free credits · {fmt.n(Math.round(row.noteMinutesFreeRemaining || 0))} free mins
                            </div>
                          </td>
                          <td>
                            <div className="admin-pill-row">
                              {(row.byAction || []).map((actionRow) => (
                                <span key={`${row.companyId}-${actionRow.action}`} className="admin-pill">
                                  {formatAiAction(actionRow.action)}: {fmt.money(actionRow.costInr)}
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {filteredAiExpenseCompanies.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="muted">No AI expense records found.</td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>

                <div className={`dashboard-card admin-usage-platform-card ${aiExpensesRecentOpen ? 'is-expanded' : 'is-collapsed'}`} style={{ marginTop: 16 }}>
                  <button
                    type="button"
                    className="admin-usage-platform-toggle"
                    onClick={() => setAiExpensesRecentOpen((v) => !v)}
                    aria-expanded={aiExpensesRecentOpen}
                  >
                    <div className="admin-usage-platform-toggle-text">
                      <h3>Recent AI logs</h3>
                      <div className="muted">Last {fmt.n(aiExpenseRecent.length)} calls in range</div>
                    </div>
                  </button>
                  {aiExpensesRecentOpen ? (
                    <div className="admin-table-wrap" style={{ marginTop: 8 }}>
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>When</th>
                            <th>Company</th>
                            <th>Action</th>
                            <th>Model</th>
                            <th>Status</th>
                            <th>Tokens</th>
                            <th>Cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {aiExpenseRecent.map((row) => (
                            <tr key={row.id}>
                              <td className="muted">{formatDateTime(row.createdAt)}</td>
                              <td>{row.companyName || row.companyId}</td>
                              <td>{formatAiAction(row.action)}</td>
                              <td className="muted">{row.model || '-'}</td>
                              <td>{row.status || '-'}</td>
                              <td>{fmt.n(row.totalTokens)}</td>
                              <td style={{ fontWeight: 700 }}>{fmt.money(row.costInr)}</td>
                            </tr>
                          ))}
                          {aiExpenseRecent.length === 0 ? (
                            <tr>
                              <td colSpan={7} className="muted">No recent logs.</td>
                            </tr>
                          ) : null}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </div>
              </>
            )}
            {error && <div className="admin-error" role="alert">{error}</div>}
          </>
        ) : tab === 'billing' ? (
          <>
            <div className="admin-inline-controls">
              <div className="control-group">
                <label>Search company</label>
                <input
                  type="text"
                  value={billingSearch}
                  placeholder="Name or email"
                  onChange={(e) => setBillingSearch(e.target.value)}
                />
              </div>
              <div className="control-group">
                <label>Plan status</label>
                <select value={billingPlanStatus} onChange={(e) => setBillingPlanStatus(e.target.value)}>
                  <option value="all">All</option>
                  <option value="trial">Trial</option>
                  <option value="active">Active</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
              <div className="control-group">
                <label>Latest bill</label>
                <select value={billingPaymentStatus} onChange={(e) => setBillingPaymentStatus(e.target.value)}>
                  <option value="all">All</option>
                  <option value="paid">Paid</option>
                  <option value="due">Due</option>
                  <option value="failed">Failed</option>
                  <option value="retrying">Retrying</option>
                  <option value="open">Open (due/failed/retrying)</option>
                </select>
              </div>
              <button type="button" className="btn-secondary small" onClick={loadBilling} disabled={billingLoading}>
                Refresh
              </button>
            </div>

            <div className="admin-billing-dashboard">
              <div className="admin-stats-grid admin-billing-stats-grid">
                <div className="dashboard-card">
                  <h3>Total collected</h3>
                  <div className="stat-value">{fmt.money(billingSummary.totalCollected)}</div>
                  <div className="muted">Sum of paid Attendance bills</div>
                </div>
                <div className="dashboard-card">
                  <h3>Open dues</h3>
                  <div className="stat-value">{fmt.money(billingSummary.openDues)}</div>
                  <div className="muted">Due / failed / retrying bills</div>
                </div>
                <div className="dashboard-card">
                  <h3>Paid companies</h3>
                  <div className="stat-value">{fmt.n(billingSummary.paidCompanies)}</div>
                  <div className="muted">Active plans: {fmt.n(billingSummary.activePlans)} | Trials: {fmt.n(billingSummary.trialPlans)}</div>
                </div>
                <div className="dashboard-card">
                  <h3>Open / failed bills</h3>
                  <div className="stat-value">{fmt.n(billingSummary.openBillCount)}</div>
                  <div className="muted">Filtered companies: {fmt.n(billingSummary.companyCount)}</div>
                </div>
              </div>
            </div>

            {billingLoading ? (
              <div className="modern-loading-state">
                <div className="loading-spinner-container">
                  <div className="loading-spinner modern" />
                  <div className="loading-text">
                    <h3>Loading billing</h3>
                    <p>Fetching Attendance plans, bills, and invoices.</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Plan</th>
                      <th>Seats</th>
                      <th>Next due</th>
                      <th>Latest bill</th>
                      <th>Collected</th>
                      <th>Upcoming</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBillingCompanies.map((company) => {
                      const totalSpend = Number(company.totalPaidAmount || 0);
                      const latestStatus = company.latestBillStatus || null;
                      const latestKind = company.latestBillKind || null;
                      const latestAmount = company.latestBillAmount;
                      const latestAt = company.latestBillAt;
                      const seatsUsed = Number(company.seatsUsed ?? ((company.employeesUsed || 0) + (company.vehiclesUsed || 0)));
                      const seatCapacity = Number(company.seatCapacity ?? company.employeeSeats ?? 0);
                      const paidSeats = Number(company.paidSeats ?? company.employeeSeats ?? 0);
                      const freeSeats = company.freeSeats != null
                        ? Number(company.freeSeats)
                        : Math.max(0, seatCapacity - paidSeats);
                      const historyCount = Number(company.historyCount ?? ((company.billCount || 0) + (company.paymentCount || 0)));
                      const upcoming = getUpcomingPayment(company);

                      return (
                      <React.Fragment key={company.companyId}>
                        <tr>
                          <td>
                            <div style={{ fontWeight: 700 }}>{company.companyName}</div>
                            <div className="muted">{company.email || '-'}</div>
                          </td>
                          <td>
                            <div style={{ fontWeight: 700 }}>{company.planStatus || '-'}</div>
                            <div className="muted">{company.billingCycle || '-'}</div>
                            {company.hasAutopay ? (
                              <div className="muted">Autopay: {company.subscriptionStatus || 'ACTIVE'}</div>
                            ) : null}
                            <div className="muted" style={{ marginTop: 4 }}>
                              Trial: {company.trialEndsAt ? formatDateOnly(company.trialEndsAt) : '-'}
                            </div>
                            <div className="muted" style={{ marginTop: 4 }}>
                              Billing: {company.billingMode === 'offline' ? 'Offline' : 'Online'}
                              {' · '}
                              <button
                                type="button"
                                className="admin-link-btn"
                                disabled={Boolean(billingModeSavingByCompanyId[company.companyId])}
                                onClick={() => handleToggleBillingMode(
                                  company.companyId,
                                  company.billingMode === 'offline' ? 'online' : 'offline'
                                )}
                              >
                                {billingModeSavingByCompanyId[company.companyId]
                                  ? 'Saving…'
                                  : (company.billingMode === 'offline' ? 'Switch to online' : 'Switch to offline')}
                              </button>
                            </div>
                            {trialEditorCompanyId === company.companyId ? (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6, alignItems: 'center' }}>
                                <input
                                  type="date"
                                  value={trialDraftByCompanyId[company.companyId] || ''}
                                  onChange={(e) => setTrialDraftByCompanyId((prev) => ({
                                    ...prev,
                                    [company.companyId]: e.target.value
                                  }))}
                                  disabled={Boolean(trialSavingByCompanyId[company.companyId])}
                                />
                                <button
                                  type="button"
                                  className="admin-link-btn"
                                  onClick={() => handleSaveTrialDate(company.companyId)}
                                  disabled={Boolean(trialSavingByCompanyId[company.companyId])}
                                >
                                  {trialSavingByCompanyId[company.companyId] ? 'Saving…' : 'Save trial'}
                                </button>
                                <button
                                  type="button"
                                  className="admin-link-btn"
                                  onClick={() => setTrialEditorCompanyId(null)}
                                  disabled={Boolean(trialSavingByCompanyId[company.companyId])}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="admin-link-btn"
                                style={{ marginTop: 4 }}
                                onClick={() => {
                                  setTrialEditorCompanyId(company.companyId);
                                  setTrialDraftByCompanyId((prev) => ({
                                    ...prev,
                                    [company.companyId]: company.trialEndsAt
                                      ? toDateInputValue(new Date(company.trialEndsAt))
                                      : (prev[company.companyId] || toDateInputValue(new Date()))
                                  }));
                                }}
                              >
                                Set trial date
                              </button>
                            )}
                          </td>
                          <td>
                            <div style={{ fontWeight: 700 }}>{fmt.n(seatsUsed)} / {fmt.n(seatCapacity)} used</div>
                            <div className="muted">{fmt.n(paidSeats)} paid + {fmt.n(freeSeats)} free</div>
                            <div className="muted">
                              {fmt.n(company.employeesUsed || 0)} employees · {fmt.n(company.vehiclesUsed || 0)} vehicles
                            </div>
                          </td>
                          <td className="muted">
                            {company.periodEnd ? formatDateOnly(company.periodEnd) : (company.trialEndsAt ? `Trial ends ${formatDateOnly(company.trialEndsAt)}` : '-')}
                          </td>
                          <td>
                            <div style={{ fontWeight: 700 }}>{latestStatus || '-'}</div>
                            <div className="muted">
                              {[
                                latestKind ? formatBillKind(latestKind) : null,
                                Number(latestAmount) > 0 || latestStatus ? fmt.money(latestAmount || 0) : null
                              ].filter(Boolean).join(' • ') || '-'}
                            </div>
                            <div className="muted">{formatDateTime(latestAt)}</div>
                          </td>
                          <td>
                            <div style={{ fontWeight: 700 }}>{fmt.money(totalSpend)}</div>
                            <div className="muted">History: {fmt.n(historyCount)}</div>
                          </td>
                          <td>
                            <div style={{ fontWeight: 700 }}>
                              {upcoming.base != null ? fmt.money(upcoming.base) : (upcoming.amount != null ? fmt.money(upcoming.amount) : '-')}
                            </div>
                            <div className="muted">
                              {upcoming.gst != null ? `GST ${fmt.money(upcoming.gst)}` : '-'}
                            </div>
                            <div className="muted">{upcoming.dueLabel || '-'}</div>
                          </td>
                          <td>
                            <button type="button" className="admin-link-btn" onClick={() => openBillingCompany(company.companyId)}>
                              {expandedBillingCompanyId === company.companyId ? 'Hide bills' : 'View bills'}
                            </button>
                          </td>
                        </tr>

                        {expandedBillingCompanyId === company.companyId ? (
                          <tr className="admin-nested">
                            <td colSpan={8}>
                              {billingDetailsLoadingByCompanyId[company.companyId] ? (
                                <div style={{ padding: 12 }}>
                                  <div className="loading-spinner modern" style={{ width: 28, height: 28, borderWidth: 3 }} />
                                </div>
                              ) : (
                                <div style={{ margin: '8px 0' }}>
                                  {(() => {
                                    const offlineForm = offlineDocFormByCompanyId[company.companyId] || emptyOfflineDocForm();
                                    const isEditingDoc = Boolean(offlineForm.editingId);
                                    return (
                                      <>
                                  <div style={{ fontWeight: 700, marginBottom: 8 }}>
                                    Offline documents
                                    {isEditingDoc ? (
                                      <span className="muted" style={{ fontWeight: 500, marginLeft: 8 }}>
                                        Editing #{offlineForm.documentNumber || offlineForm.editingId}
                                      </span>
                                    ) : null}
                                  </div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end', marginBottom: 10 }}>
                                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                                      PDF / image{isEditingDoc ? ' (optional replace)' : ''}
                                      <input
                                        type="file"
                                        accept="application/pdf,image/*"
                                        disabled={Boolean(offlineDocBusyByCompanyId[company.companyId])}
                                        onChange={(e) => {
                                          const file = e.target.files?.[0];
                                          handleOfflineDocOcr(company.companyId, file);
                                          e.target.value = '';
                                        }}
                                      />
                                      {isEditingDoc && offlineForm.hasExistingFile && !offlineForm.s3Key ? (
                                        <span className="muted">Keeping existing PDF</span>
                                      ) : null}
                                    </label>
                                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                                      Kind
                                      <select
                                        value={offlineForm.kind}
                                        onChange={(e) => handleOfflineDocField(company.companyId, 'kind', e.target.value)}
                                      >
                                        <option value="invoice">Invoice</option>
                                        <option value="proforma">Proforma</option>
                                      </select>
                                    </label>
                                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                                      #
                                      <input
                                        type="text"
                                        value={offlineForm.documentNumber}
                                        onChange={(e) => handleOfflineDocField(company.companyId, 'documentNumber', e.target.value)}
                                      />
                                    </label>
                                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, minWidth: 180 }}>
                                      Description
                                      <input
                                        type="text"
                                        value={offlineForm.description}
                                        onChange={(e) => handleOfflineDocField(company.companyId, 'description', e.target.value)}
                                      />
                                    </label>
                                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                                      Amount
                                      <input
                                        type="number"
                                        step="0.01"
                                        value={offlineForm.amount}
                                        onChange={(e) => handleOfflineDocField(company.companyId, 'amount', e.target.value)}
                                      />
                                    </label>
                                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                                      <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                        Due on
                                        <button
                                          type="button"
                                          className="admin-link-btn"
                                          style={{ padding: 0, fontSize: 11 }}
                                          onClick={() => handleOfflineDocField(company.companyId, 'dueOn', '')}
                                        >
                                          Clear
                                        </button>
                                      </span>
                                      <input
                                        type="date"
                                        value={offlineForm.dueOn}
                                        onChange={(e) => handleOfflineDocField(company.companyId, 'dueOn', e.target.value)}
                                      />
                                    </label>
                                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                                      <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                        Paid on
                                        <button
                                          type="button"
                                          className="admin-link-btn"
                                          style={{ padding: 0, fontSize: 11 }}
                                          onClick={() => handleOfflineDocField(company.companyId, 'paidOn', '')}
                                        >
                                          Clear
                                        </button>
                                      </span>
                                      <input
                                        type="date"
                                        value={offlineForm.paidOn}
                                        onChange={(e) => handleOfflineDocField(company.companyId, 'paidOn', e.target.value)}
                                      />
                                    </label>
                                    <button
                                      type="button"
                                      className="admin-link-btn"
                                      disabled={Boolean(offlineDocBusyByCompanyId[company.companyId])}
                                      onClick={() => handleSaveOfflineDoc(company.companyId)}
                                    >
                                      {offlineDocBusyByCompanyId[company.companyId]
                                        ? 'Working…'
                                        : (isEditingDoc ? 'Update document' : 'Save document')}
                                    </button>
                                    {isEditingDoc ? (
                                      <button
                                        type="button"
                                        className="admin-link-btn"
                                        disabled={Boolean(offlineDocBusyByCompanyId[company.companyId])}
                                        onClick={() => handleCancelOfflineDocEdit(company.companyId)}
                                      >
                                        Cancel edit
                                      </button>
                                    ) : null}
                                  </div>
                                  <div className="admin-table-wrap" style={{ marginBottom: 16 }}>
                                    <table className="admin-table">
                                      <thead>
                                        <tr>
                                          <th>#</th>
                                          <th>Kind</th>
                                          <th>Description</th>
                                          <th>Due / Paid</th>
                                          <th>Amount</th>
                                          <th />
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(offlineDocsByCompanyId[company.companyId] || []).map((doc) => (
                                          <tr key={`offline-doc-${doc.id}`}>
                                            <td style={{ fontWeight: 700 }}>{doc.documentNumber}</td>
                                            <td className="muted">{doc.kind}</td>
                                            <td className="muted">{doc.description || '-'}</td>
                                            <td className="muted">
                                              {[
                                                doc.dueOn ? `Due ${formatDateOnly(doc.dueOn)}` : null,
                                                doc.paidOn ? `Paid ${formatDateOnly(doc.paidOn)}` : null
                                              ].filter(Boolean).join(' · ') || '-'}
                                            </td>
                                            <td style={{ fontWeight: 700 }}>{fmt.money(doc.amount)}</td>
                                            <td>
                                              {doc.openUrl ? (
                                                <button
                                                  type="button"
                                                  className="admin-link-btn"
                                                  onClick={() => window.open(doc.openUrl, '_blank', 'noopener,noreferrer')}
                                                >
                                                  Open
                                                </button>
                                              ) : null}
                                              {' '}
                                              <button
                                                type="button"
                                                className="admin-link-btn"
                                                onClick={() => handleEditOfflineDoc(company.companyId, doc)}
                                              >
                                                Edit
                                              </button>
                                              {' '}
                                              <button
                                                type="button"
                                                className="admin-link-btn"
                                                onClick={() => handleDeleteOfflineDoc(company.companyId, doc.id)}
                                              >
                                                Delete
                                              </button>
                                            </td>
                                          </tr>
                                        ))}
                                        {(offlineDocsByCompanyId[company.companyId] || []).length === 0 ? (
                                          <tr>
                                            <td colSpan={6} className="muted">No offline documents yet.</td>
                                          </tr>
                                        ) : null}
                                      </tbody>
                                    </table>
                                  </div>
                                      </>
                                    );
                                  })()}

                                  <div style={{ fontWeight: 700, marginBottom: 8 }}>Online / ledger bills</div>
                                  <div className="admin-table-wrap">
                                    <table className="admin-table">
                                      <thead>
                                        <tr>
                                          <th>Kind</th>
                                          <th>Period</th>
                                          <th>Status</th>
                                          <th>Amount</th>
                                          <th>Paid at</th>
                                          <th>Payment</th>
                                          <th>Invoice</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(billingDetailsByCompanyId[company.companyId] || []).map((bill) => (
                                          <tr key={`${bill.source || 'bill'}-${bill.id}`}>
                                            <td>
                                              <div style={{ fontWeight: 700 }}>{formatBillKind(bill.kind)}</div>
                                              <div className="muted">
                                                {bill.source === 'plan_payment' ? 'Legacy payment' : (bill.billingCycle || '-')}
                                              </div>
                                            </td>
                                            <td className="muted">{formatPeriodRange(bill.periodStart, bill.periodEnd)}</td>
                                            <td style={{ fontWeight: 700 }}>{bill.status || '-'}</td>
                                            <td>
                                              <div style={{ fontWeight: 700 }}>{fmt.money(bill.totalAmount)}</div>
                                              <div className="muted">
                                                Base {fmt.money(bill.amount)} + GST {fmt.money(bill.gstAmount)}
                                              </div>
                                            </td>
                                            <td className="muted">{formatDateTime(bill.paidAt)}</td>
                                            <td className="muted">
                                              <div>{bill.paymentMode || bill.paymentMethod || '-'}</div>
                                              {bill.pgOrderId ? <div>PG: {bill.pgOrderId}</div> : null}
                                              {bill.paymentReferenceId ? <div>Ref: {bill.paymentReferenceId}</div> : null}
                                            </td>
                                            <td>
                                              <div style={{ fontWeight: 700 }}>{bill.invoiceNumber || '-'}</div>
                                              <div className="muted">{formatDateTime(bill.invoiceGeneratedAt)}</div>
                                              {bill.invoiceUrl ? (
                                                <button
                                                  type="button"
                                                  className="admin-link-btn"
                                                  onClick={() => window.open(bill.invoiceUrl, '_blank', 'noopener,noreferrer')}
                                                >
                                                  Open invoice
                                                </button>
                                              ) : null}
                                            </td>
                                          </tr>
                                        ))}
                                        {(billingDetailsByCompanyId[company.companyId] || []).length === 0 ? (
                                          <tr>
                                            <td colSpan={7} className="muted">No billing history found.</td>
                                          </tr>
                                        ) : null}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                      );
                    })}

                    {filteredBillingCompanies.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="muted">No billing records found.</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            )}
            {error && <div className="admin-error" role="alert">{error}</div>}
          </>
        ) : (
          <>
            <div className="admin-inline-controls">
              <div className="control-group">
                <label>Start date</label>
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: e.target.value }))}
                />
              </div>
              <div className="control-group">
                <label>End date</label>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value }))}
                />
              </div>
              <div className="control-group checkbox">
                <input
                  id="includeTripTotals"
                  type="checkbox"
                  checked={includeTripTotals}
                  onChange={(e) => setIncludeTripTotals(e.target.checked)}
                />
                <label htmlFor="includeTripTotals">Include trip totals</label>
              </div>
              <button type="button" className="btn-secondary small" onClick={loadStats} disabled={loading}>
                Refresh
              </button>
            </div>

            <div className="admin-stats-grid">
              <div className="dashboard-card">
                <h3>Companies</h3>
                <div className="stat-value">{fmt.n(overview?.totals?.companyCount || 0)}</div>
              </div>
              <div className="dashboard-card">
                <h3>Employees</h3>
                <div className="stat-value">{fmt.n(overview?.totals?.employeeCount || 0)}</div>
              </div>
              <div className="dashboard-card">
                <h3>Supervisors</h3>
                <div className="stat-value">{fmt.n(overview?.totals?.supervisorCount || 0)}</div>
              </div>
            </div>

            {loading ? (
              <div className="modern-loading-state">
                <div className="loading-spinner-container">
                  <div className="loading-spinner modern" />
                  <div className="loading-text">
                    <h3>Loading stats</h3>
                    <p>Computing trips may take a moment for larger ranges.</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Status</th>
                      <th>Last active</th>
                      <th>Employees</th>
                      <th>Supervisors</th>
                      <th>Trips</th>
                      <th>Distance (km)</th>
                      <th>Duration (min)</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {companies.map((company) => (
                      <React.Fragment key={company.companyId}>
                        <tr>
                          <td>
                            <div style={{ fontWeight: 700 }}>{company.name}</div>
                            {company.email ? <div className="muted">{company.email}</div> : null}
                          </td>
                          <td>{company.isActive ? 'Active' : 'Inactive'}</td>
                          <td className="muted">{formatDateTime(company.lastActiveAt)}</td>
                          <td>{fmt.n(company.employeeCount)}</td>
                          <td>{fmt.n(company.supervisorCount)}</td>
                          <td>{includeTripTotals ? fmt.n(company.tripTotals?.tripCount || 0) : <span className="muted">-</span>}</td>
                          <td>{includeTripTotals ? fmt.km(company.tripTotals?.distanceKm || 0) : <span className="muted">-</span>}</td>
                          <td>{includeTripTotals ? fmt.n(company.tripTotals?.durationMin || 0) : <span className="muted">-</span>}</td>
                          <td>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                              <button type="button" className="admin-link-btn" onClick={() => openCompany(company.companyId, 'employees')}>
                                {expandedCompanyId === company.companyId && expandedPanel === 'employees' ? 'Hide employees' : 'View employees'}
                              </button>
                              <button type="button" className="admin-link-btn" onClick={() => openCompany(company.companyId, 'supervisors')}>
                                {expandedCompanyId === company.companyId && expandedPanel === 'supervisors' ? 'Hide supervisors' : 'View supervisors'}
                              </button>
                            </div>
                          </td>
                        </tr>

                        {expandedCompanyId === company.companyId ? (
                          <tr className="admin-nested">
                            <td colSpan={9}>
                              {employeesLoadingByCompanyId[company.companyId] ? (
                                <div style={{ padding: 12 }}>
                                  <div className="loading-spinner modern" style={{ width: 28, height: 28, borderWidth: 3 }} />
                                </div>
                              ) : (
                                <div className="admin-table-wrap" style={{ margin: '8px 0' }}>
                                  {expandedPanel === 'employees' ? (
                                    <table className="admin-table">
                                      <thead>
                                        <tr>
                                          <th>Employee</th>
                                          <th>Email</th>
                                          <th>Status</th>
                                          <th>Last active</th>
                                          <th>Trips</th>
                                          <th>Distance (km)</th>
                                          <th>Duration (min)</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(employeesByCompanyId[company.companyId] || []).map((employee) => (
                                          <tr key={employee.employeeId}>
                                            <td style={{ fontWeight: 700 }}>{employee.name || `Employee #${employee.employeeId}`}</td>
                                            <td className="muted">{employee.email || '-'}</td>
                                            <td>{employee.isActive ? 'Active' : 'Inactive'}</td>
                                            <td className="muted">{formatDateTime(employee.lastActiveAt)}</td>
                                            <td>{fmt.n(employee.tripTotals?.tripCount || 0)}</td>
                                            <td>{fmt.km(employee.tripTotals?.distanceKm || 0)}</td>
                                            <td>{fmt.n(employee.tripTotals?.durationMin || 0)}</td>
                                          </tr>
                                        ))}
                                        {(!employeesByCompanyId[company.companyId] || employeesByCompanyId[company.companyId].length === 0) ? (
                                          <tr>
                                            <td colSpan={7} className="muted">No employees found.</td>
                                          </tr>
                                        ) : null}
                                      </tbody>
                                    </table>
                                  ) : (
                                    <table className="admin-table">
                                      <thead>
                                        <tr>
                                          <th>Supervisor</th>
                                          <th>Email</th>
                                          <th>Role</th>
                                          <th>Status</th>
                                          <th>Last active</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(supervisorsByCompanyId[company.companyId] || []).map((supervisor) => (
                                          <tr key={supervisor.userId}>
                                            <td style={{ fontWeight: 700 }}>{supervisor.name || `User #${supervisor.userId}`}</td>
                                            <td className="muted">{supervisor.email || '-'}</td>
                                            <td>{supervisor.role}</td>
                                            <td>{supervisor.isActive ? 'Active' : 'Inactive'}</td>
                                            <td className="muted">{formatDateTime(supervisor.lastActiveAt)}</td>
                                          </tr>
                                        ))}
                                        {(!supervisorsByCompanyId[company.companyId] || supervisorsByCompanyId[company.companyId].length === 0) ? (
                                          <tr>
                                            <td colSpan={5} className="muted">No supervisors found.</td>
                                          </tr>
                                        ) : null}
                                      </tbody>
                                    </table>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    ))}

                    {companies.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="muted">No companies.</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            )}

            {error && <div className="admin-error" role="alert">{error}</div>}
          </>
        )}
      </div>
    </div>
  );
};

export default Admin;